import React, { useState, useEffect, useRef } from 'react';
import { GameStatus, GameState, CaseData, Location, LogEntry, Character, StoreItem, Announcement } from '../../types';
import { generateCase, interrogateCharacter, getTutorialCase } from '../../services/geminiService';
import { useAuth, db, functions, logout as firebaseLogout, registerPushNotifications } from '../../services/firebase';
import { doc, getDoc, setDoc, query, collection, where, getDocs, updateDoc, onSnapshot, orderBy } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { Clock } from '../Clock';
import { LocationCard } from '../LocationCard';
import { OnboardingPopups } from './OnboardingPopups';
import { Car, Search, MessageSquare, Map as MapIcon, Radio, PenTool, User, Fingerprint, FolderOpen, History, Siren, Bell, X, BadgeAlert, Briefcase, Phone, PhoneCall, ArrowRight, FileText, Maximize2, Moon, Star, Zap, Lock, ChevronLeft, Archive, Skull, PlayCircle, Calendar, Shield, Eye, EyeOff, LogOut, ShoppingBag, ShoppingCart, Medal, RefreshCw, AlertTriangle } from 'lucide-react';

const HQ_LOCATION: Location = {
    id: 'HQ',
    name: 'Quartel General',
    description: 'Seu escritório seguro. Onde os casos começam.',
    evidence: 'Apenas pilhas de papelada, café frio e o cheiro de cigarros baratos. O telefone toca ocasionalmente.',
    openTime: '00:00',
    closeTime: '23:59',
    x: 50, // Center of map
    y: 50,
    type: 'public',
    isCrimeScene: false
};

const INTERROGATION_COOLDOWN_SEC = 2;
const START_DATE_ISO = "2024-01-01T08:00:00";

type Tab = 'leftPanel' | 'investigation';
type LeftPanelTab = 'map' | 'notes' | 'suspects' | 'clues' | 'history';
type AcmeTab = 'active' | 'archived' | 'lost';

const ROLE_MAP: Record<string, string> = {
    witness: 'TESTEMUNHA',
    suspect: 'SUSPEITO',
    expert: 'ESPECIALISTA'
};

interface AppNotification {
    id: number;
    title: string;
    message: string;
    type: 'info' | 'success' | 'alert';
}

export const Game: React.FC = () => {
    const { user: authUser } = useAuth();
    const [gameState, setGameState] = useState<GameState>({
        status: GameStatus.DASHBOARD,
        dashboardView: 'main',
        activeCases: [],
        archivedCases: [],
        selectedCaseId: null,
        travelStatus: 'idle',
        travelDestinationId: null,
        travelArrivalTime: null,
        travelTotalDuration: null,
        nextActionTime: null,
        gameTime: new Date(START_DATE_ISO).getTime(),
        viewedTutorials: []
    });
    const [isTestMode, setIsTestMode] = useState(false);

    const [interactionText, setInteractionText] = useState('');
    const [inputQuery, setInputQuery] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [timeLeft, setTimeLeft] = useState<number>(0);
    const [cooldownLeft, setCooldownLeft] = useState<number>(0);
    const [activeTab, setActiveTab] = useState<Tab>('leftPanel');
    const [leftPanelTab, setLeftPanelTab] = useState<LeftPanelTab>('map');
    const [acmeTab, setAcmeTab] = useState<AcmeTab>('active');
    const [notifications, setNotifications] = useState<AppNotification[]>([]);
    const [arrestModalOpen, setArrestModalOpen] = useState<string | null>(null);
    const [selectedLog, setSelectedLog] = useState<LogEntry | null>(null);
    const [timeFeedback, setTimeFeedback] = useState<{ label: string, id: number } | null>(null);
    const [corregedoriaModal, setCorregedoriaModal] = useState<{repLost: number, newRep: number, rankName?: string, downranked: boolean} | null>(null);
    const [promotionModal, setPromotionModal] = useState<{newRankName: string, rewardMsg: string} | null>(null);
    const [showPushModal, setShowPushModal] = useState(false);
    const [selectedCaseModal, setSelectedCaseModal] = useState<CaseData | null>(null);
    const [globalCases, setGlobalCases] = useState<CaseData[]>([]);
    const [staticTutorial, setStaticTutorial] = useState<CaseData | null>(null);
    const [gameCategories, setGameCategories] = useState<string[]>([]);
    const [heroIndex, setHeroIndex] = useState(0);
    const [showWelcomeTutorial, setShowWelcomeTutorial] = useState(false);

    // Announcement State
    const [announcements, setAnnouncements] = useState<Announcement[]>([]);
    const [unreadAnnouncementsCount, setUnreadAnnouncementsCount] = useState(0);
    const [announcementModalOpen, setAnnouncementModalOpen] = useState(false);
    const hasAutoOpenedAnnouncements = useRef<boolean>(false);

    // Store State
    const [storeItems, setStoreItems] = useState<StoreItem[]>([]);
    const [isStoreOpen, setIsStoreOpen] = useState(false);
    const [storeTab, setStoreTab] = useState<'products' | 'history'>('products');
    const [userSales, setUserSales] = useState<any[]>([]);

    // Checkout State
    const [checkoutItem, setCheckoutItem] = useState<StoreItem | null>(null);
    const [cpfInput, setCpfInput] = useState('');
    const [isCheckingOut, setIsCheckingOut] = useState(false);
    const [checkoutError, setCheckoutError] = useState('');
    const [pixData, setPixData] = useState<{ encodedImage: string, payload: string, paymentId: string } | null>(null);
    const [checkoutTimeLeft, setCheckoutTimeLeft] = useState<number>(300);

    const hasLoadedAnyRef = useRef<boolean>(false);
    const syncedUserIdRef = useRef<string | "local" | null>(null);

    useEffect(() => {
        if (!db) return;
        getDoc(doc(db, 'settings', 'gameplay')).then(snap => {
            if (snap.exists() && snap.data().categories) {
                setGameCategories(snap.data().categories);
            }
        });
    }, []);

    // Auto-Rotate featured hero image
    useEffect(() => {
        const activeOrArchivedIds = new Set([...gameState.activeCases.map(c => c.id), ...gameState.archivedCases.map(c => c.id)]);
        const unseenCases = globalCases.filter(c => c.id !== 'tutorial-case-001' && !activeOrArchivedIds.has(c.id));
        const featuredCount = Math.min(unseenCases.length, 5);

        if (featuredCount <= 1) return;
        const interval = setInterval(() => {
            setHeroIndex(prev => (prev + 1) % featuredCount);
        }, 5000);
        return () => clearInterval(interval);
    }, [globalCases, gameState.activeCases, gameState.archivedCases]);

    useEffect(() => {
        getTutorialCase().then(setStaticTutorial);
        if (!db) return;
        const q = query(
            collection(db, 'cases'),
            where('publishStatus', '==', 'published')
        );
        const unsubscribe = onSnapshot(q, (snapshot) => {
            const list = snapshot.docs.map(doc => doc.data() as CaseData);
            // Default sort by scheduledDate if exists, else by createdAt descending
            list.sort((a, b) => {
                const dateA = a.scheduledDate || a.createdAt;
                const dateB = b.scheduledDate || b.createdAt;
                return dateB - dateA;
            });
            setGlobalCases(list);
        });

        // Trigger Welcome Tutorial for brand new users
        const isFirstAccess = gameState.activeCases.length === 0 && 
                            gameState.archivedCases.length === 0 && 
                            !(gameState.viewedTutorials || []).includes('tut_welcome');
        
        if (isFirstAccess && syncedUserIdRef.current !== null) {
            setShowWelcomeTutorial(true);
        }

        // Listen for Store Items
        const qStore = query(collection(db, 'store_items'), where('isActive', '==', true));
        const unsubscribeStore = onSnapshot(qStore, (snapshot) => {
            const items = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() } as StoreItem));
            setStoreItems(items);
        });

        // Listen for Announcements
        const qAnnouncements = query(collection(db, 'announcements'), where('isActive', '==', true), orderBy('createdAt', 'desc'));
        const unsubscribeAnn = onSnapshot(qAnnouncements, (snapshot) => {
            const list = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() } as Announcement));
            const valid = list.filter(a => !a.expiresAt || a.expiresAt.toMillis() > Date.now());
            setAnnouncements(valid);

            const readIds: string[] = JSON.parse(localStorage.getItem('chronos_read_announcements') || '[]');
            const unread = valid.filter(a => !readIds.includes(a.id!));
            setUnreadAnnouncementsCount(unread.length);

            // Auto popup se tiver recados não lidos (1x por sessão)
            if (unread.length > 0 && !hasAutoOpenedAnnouncements.current) {
                setAnnouncementModalOpen(true);
                const ids = valid.map(a => a.id!);
                localStorage.setItem('chronos_read_announcements', JSON.stringify(ids));
                setUnreadAnnouncementsCount(0);
                hasAutoOpenedAnnouncements.current = true;
            }
        });

        return () => { unsubscribe(); unsubscribeStore(); unsubscribeAnn(); };
    }, []);

    useEffect(() => {
        if (!db || !isStoreOpen || !authUser) return;
        const qSales = query(collection(db, 'sales'), where('buyerId', '==', authUser.uid));
        const unsubscribeSales = onSnapshot(qSales, (snapshot) => {
            let history = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));

            // Ordenação local para exibir pendentes/cancelados que talvez não tenham 'purchasedAt'
            history.sort((a: any, b: any) => {
                const dateA = a.purchasedAt || a.createdAt || { toMillis: () => 0 };
                const dateB = b.purchasedAt || b.createdAt || { toMillis: () => 0 };
                return dateB.toMillis() - dateA.toMillis();
            });

            setUserSales(history);
        });
        return () => unsubscribeSales();
    }, [isStoreOpen, authUser]);

    useEffect(() => {
        if (authUser && 'Notification' in window && Notification.permission === 'default') {
            const timer = setTimeout(() => setShowPushModal(true), 3500);
            return () => clearTimeout(timer);
        }
    }, [authUser]);

    const handleAcceptPush = async () => {
        setShowPushModal(false);
        if (!authUser) return;
        try {
            await registerPushNotifications(authUser.uid);
            showNotification("COMUNICAÇÃO ESTABELECIDA", "O rádio da Agência foi sintonizado com sucesso.", "success");
        } catch (err) {
            console.error(err);
            showNotification("FALHA DE COMUNICAÇÃO", "Não foi possível sintonizar a frequência.", "alert");
        }
    };

    // LOAD STATE
    useEffect(() => {
        const loadGame = async () => {
            let loaded = false;

            // 0. Test Mode Detection
            const urlParams = new URLSearchParams(window.location.search);
            if (urlParams.get('test') === 'true') {
                const testCaseData = localStorage.getItem('chronos_test_case_data');
                if (testCaseData) {
                    try {
                        const parsedCase = JSON.parse(testCaseData);
                        setGameState(prev => ({
                            ...prev,
                            status: GameStatus.INVESTIGATING,
                            activeCases: [parsedCase],
                            selectedCaseId: parsedCase.id,
                            gameTime: new Date(START_DATE_ISO).getTime()
                        }));
                        setIsTestMode(true);
                        loaded = true;
                        console.log("Game loaded in TEST MODE");
                    } catch (e) {
                        console.error("Test mode load failed", e);
                    }
                }
            }

            // 1. Try Firestore if logged in (only if not in test mode)
            if (!loaded && authUser && db) {
                try {
                    const docRef = doc(db, "users", authUser.uid);
                    const docSnap = await getDoc(docRef);

                    if (docSnap.exists() && docSnap.data().gameState) {
                        const parsed = docSnap.data().gameState;
                        // ... applying migrations if needed (same logic as below) ...
                        if (!parsed.gameTime) parsed.gameTime = new Date(START_DATE_ISO).getTime();
                        if (!parsed.dashboardView) parsed.dashboardView = 'main';
                        if (!parsed.viewedTutorials) parsed.viewedTutorials = [];
                        parsed.activeCases = parsed.activeCases.map((c: any) => ({
                            ...c,
                            lastSavedTime: c.lastSavedTime || parsed.gameTime,
                            scoutedLocationIds: c.scoutedLocationIds || ['HQ'],
                            knownCharacterNames: c.knownCharacterNames || {},
                            heroImageUrl: c.heroImageUrl || (c.id === 'tutorial-case-001' ? '/tutorialhero.jpg' : null),
                            thumbImageUrl: c.thumbImageUrl || (c.id === 'tutorial-case-001' ? '/tutorialcarrossel.jpg' : null)
                        }));
                        if (parsed.archivedCases) {
                            parsed.archivedCases = parsed.archivedCases.map((c: any) => ({
                                ...c,
                                heroImageUrl: c.heroImageUrl || (c.id === 'tutorial-case-001' ? '/tutorialhero.jpg' : null),
                                thumbImageUrl: c.thumbImageUrl || (c.id === 'tutorial-case-001' ? '/tutorialcarrossel.jpg' : null)
                            }));
                        }
                        setGameState(parsed);
                        loaded = true;
                        console.log("Game state loaded from Firestore");

                        // Passive Energy Regeneration Logic
                        const userData = docSnap.data();
                        if (userData.role !== 'subscriber' && userData.lastOfflineTimeForEnergy) {
                            const lastEnergyTime = userData.lastOfflineTimeForEnergy.seconds ? userData.lastOfflineTimeForEnergy.seconds * 1000 : userData.lastOfflineTimeForEnergy;
                            const msAway = Date.now() - lastEnergyTime;
                            const hoursAway = Math.floor(msAway / (1000 * 60 * 60)); // 1 hour = 1 Lupa

                            if (hoursAway > 0) {
                                const currentDaily = userData.dailyLupas || 0;
                                // Only regenerate up to the soft-cap of 15
                                if (currentDaily < MAX_LUPAS_PASSIVAS) {
                                    const newDaily = Math.min(MAX_LUPAS_PASSIVAS, currentDaily + hoursAway);

                                    // Update Firestore with new energy and reset the timer
                                    try {
                                        await updateDoc(docRef, {
                                            dailyLupas: newDaily,
                                            lastOfflineTimeForEnergy: new Date()
                                        });
                                        if (setDailyLupas) setDailyLupas(newDaily);
                                        console.log(`Passive Energy Regenerated: +${newDaily - currentDaily} Lupas`);
                                    } catch (e) { console.error("Failed to regenerate energy", e); }
                                } else {
                                    // Maxed out, just update the timer
                                    try {
                                        await updateDoc(docRef, { lastOfflineTimeForEnergy: new Date() });
                                    } catch (e) { }
                                }
                            }
                        } else if (userData.role !== 'subscriber' && !userData.lastOfflineTimeForEnergy) {
                            // Initialize timer if not exists
                            try {
                                await updateDoc(docRef, { lastOfflineTimeForEnergy: new Date() });
                            } catch (e) { }
                        }

                    }
                } catch (e) {
                    console.error("Failed to load from Firestore", e);
                }
            }

            // 2. Fallback to LocalStorage if not loaded from Cloud
            if (!loaded) {
                const savedState = localStorage.getItem('chronos_state_v3');
                if (savedState) {
                    try {
                        const parsed = JSON.parse(savedState);
                        if (!parsed.gameTime) parsed.gameTime = new Date(START_DATE_ISO).getTime();
                        if (!parsed.dashboardView) parsed.dashboardView = 'main';
                        if (!parsed.viewedTutorials) parsed.viewedTutorials = [];
                        parsed.activeCases = parsed.activeCases.map((c: any) => ({
                            ...c,
                            lastSavedTime: c.lastSavedTime || parsed.gameTime,
                            scoutedLocationIds: c.scoutedLocationIds || ['HQ'],
                            knownCharacterNames: c.knownCharacterNames || {},
                            heroImageUrl: c.heroImageUrl || (c.id === 'tutorial-case-001' ? '/tutorialhero.jpg' : null),
                            thumbImageUrl: c.thumbImageUrl || (c.id === 'tutorial-case-001' ? '/tutorialcarrossel.jpg' : null)
                        }));
                        if (parsed.archivedCases) {
                            parsed.archivedCases = parsed.archivedCases.map((c: any) => ({
                                ...c,
                                heroImageUrl: c.heroImageUrl || (c.id === 'tutorial-case-001' ? '/tutorialhero.jpg' : null),
                                thumbImageUrl: c.thumbImageUrl || (c.id === 'tutorial-case-001' ? '/tutorialcarrossel.jpg' : null)
                            }));
                        }
                        setGameState(parsed);
                    } catch (e) {
                        console.error("Failed to load save from LocalStorage", e);
                    }
                }
            }

            hasLoadedAnyRef.current = true;
            syncedUserIdRef.current = authUser ? authUser.uid : "local";
        };

        if (!isLoading) loadGame();
    }, [authUser]); // Reload if user changes

    // SAVE STATE
    useEffect(() => {
        if (!hasLoadedAnyRef.current) return;

        // Always save to LocalStorage (Instant)
        localStorage.setItem('chronos_state_v3', JSON.stringify(gameState));

        // Sync to Firestore (Debounced or Async)
        if (!isTestMode && authUser && db && syncedUserIdRef.current === authUser.uid) {
            const saveToCloud = async () => {
                try {
                    // Filter out activeCases that haven't been truly interacted with yet to save DB writes
                    const filteredGameState = {
                        ...gameState,
                        activeCases: gameState.activeCases.filter(c => c.id === 'tutorial-case-001' || c.logs.length > 1)
                    };

                    await setDoc(doc(db, "users", authUser.uid), {
                        gameState: filteredGameState,
                        lastUpdated: new Date()
                    }, { merge: true });
                } catch (e) {
                    console.error("Failed to save to Cloud", e);
                }
            };
            // Simple debounce using timeout could be better, but for now just fire and forget
            // To avoid spamming, we could use a ref to track pending saves, but let's keep it simple relevant to "turns"
            saveToCloud();
        }
    }, [gameState, authUser]);

    const formatTimeLog = (timestamp: number) => {
        return new Date(timestamp).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });
    };

    const getDayLabel = (timestamp: number) => {
        const startRef = new Date(START_DATE_ISO).getTime();
        const day = Math.floor((timestamp - startRef) / (24 * 60 * 60 * 1000)) + 1;
        return `Dia ${day}`;
    };

    // HUD & VIP Logic
    const {
        role, vipExpiresAt,
        dailyLupas, setDailyLupas,
        extraLupas,
        unlockedCases, setUnlockedCases,
        adLupas, setAdLupas,
        adUsesToday, setAdUsesToday,
        archiveKeys, setArchiveKeys,
        dailyAdUnlocks, setDailyAdUnlocks,
        dailyAcmeCreations, setDailyAcmeCreations,
        reputacao, setReputacao,
        patentesRecompensadas, setPatentesRecompensadas,
        setLastCaseInteractionDate
    } = useAuth();
    const totalLupas = (dailyLupas || 0) + (extraLupas || 0) + (adLupas || 0);

    const updateInteractionDate = async () => {
        if (!authUser || !db || role === 'admin') return;
        try {
            const userRef = doc(db, 'users', authUser.uid);
            await updateDoc(userRef, { lastCaseInteractionDate: new Date() });
            if (setLastCaseInteractionDate) setLastCaseInteractionDate(new Date());
        } catch (e) {
            console.error("Erro ao atualizar data de interação", e);
        }
    };

    const getRankInfo = (pts: number) => {
        if (pts < 40) return { level: 1, name: 'Recruta Arquivista', min: 0, max: 39 };
        if (pts < 100) return { level: 2, name: 'Investigador Auxiliar', min: 40, max: 99 };
        if (pts < 250) return { level: 3, name: 'Detetive Particular', min: 100, max: 249 };
        if (pts < 500) return { level: 4, name: 'Inspetor de 1ª Classe', min: 250, max: 499 };
        if (pts < 1000) return { level: 5, name: 'Detetive Chefe', min: 500, max: 999 };
        return { level: 6, name: 'Comissário', min: 1000, max: 1000 }; // 1000 is min for infinite
    };

    // Effect for listening to Payment Success
    useEffect(() => {
        if (!db || !pixData?.paymentId) return;

        const saleRef = doc(db, 'sales', pixData.paymentId);
        const unsubscribePayment = onSnapshot(saleRef, (docSnap) => {
            if (docSnap.exists() && docSnap.data().status === 'approved') {
                setPixData(null);
                setIsStoreOpen(false);
                showNotification("PAGAMENTO APROVADO!", "Seu pacote foi entregue com sucesso. Obrigado por apoiar a Agência ACME.", "success");
            }
        });

        return () => unsubscribePayment();
    }, [pixData?.paymentId, db]);

    const [isProfileOpen, setIsProfileOpen] = useState(false);
    const [redeemCode, setRedeemCode] = useState('');
    const [isRedeeming, setIsRedeeming] = useState(false);

    // Modal ACME Generator
    const [isAcmeGeneratorOpen, setIsAcmeGeneratorOpen] = useState(false);
    const [acmeSettings, setAcmeSettings] = useState<{
        difficulty: 'Fácil' | 'Médio' | 'Difícil';
        duration: 'Curto' | 'Médio' | 'Longo';
        seed: string;
    }>({
        difficulty: 'Médio',
        duration: 'Médio',
        seed: ''
    });

    // --- DESBLOQUEIO E CHAVES ---
    const handleUnlockWithKey = async (caseData: CaseData) => {
        if (!db || !authUser) return;
        if ((archiveKeys || 0) < 1) {
            showNotification("ACESSO NEGADO", "Você não possui Chaves de Arquivo suficientes. Visite o setor de Suprimentos.", "alert");
            return;
        }

        setIsLoading(true);
        try {
            const userRef = doc(db, 'users', authUser.uid);
            const updatedCases = [...(unlockedCases || []), caseData.id];
            const updatedKeys = (archiveKeys || 0) - 1;

            await updateDoc(userRef, {
                unlockedCases: updatedCases,
                archiveKeys: updatedKeys
            });

            if (setUnlockedCases) setUnlockedCases(updatedCases);
            if (setArchiveKeys) setArchiveKeys(updatedKeys);

            showNotification("ARQUIVO DESBLOQUEADO", "Chave de Arquivo utilizada com sucesso. O caso agora está disponível.", "success");
            setSelectedCaseModal(null);
        } catch (error) {
            console.error(error);
            showNotification("ERRO NO SISTEMA", "Falha ao descriptografar o arquivo.", "alert");
        }
        setIsLoading(false);
    };

    // --- LÓGICA DE ADS (DESBLOQUEIO) ---
    const handleUnlockWithAd = async (caseData: CaseData) => {
        if (!db || !authUser) return;
        if ((dailyAdUnlocks || 0) >= 3) {
            showNotification("LIMITE EXCEDIDO", "Você já contatou muitos informantes hoje. Tente novamente amanhã.", "alert");
            return;
        }

        const adWindow = window.open(AD_LINK, '_blank');
        if (!adWindow || adWindow.closed || typeof adWindow.closed === 'undefined') {
            showNotification("SINAL INTERCEPTADO", "Sinal bloqueado. Desative seu protetor de conexão (Adblock) para que possamos interceptar a frequência.", "alert");
            return;
        }

        showNotification("DESCRIPTOGRAFANDO...", "Mantenha a aba do anúncio aberta ou rodando em 2º plano por no mínimo 30 segundos.", "info");

        const startTime = Date.now();
        let adFailTimeout: any;

        const targetSeconds = 30;
        const pushTimeout = setTimeout(() => {
            if (document.visibilityState !== 'visible' && 'serviceWorker' in navigator) {
                navigator.serviceWorker.ready.then(registration => {
                    registration.showNotification("Sinal Descriptografado!", {
                        body: "O arquivo está pronto. Você já pode retornar à agência.",
                        icon: "/logo.png", badge: "/logo.png"
                    });
                }).catch(() => { });
            }
        }, targetSeconds * 1000);

        const cleanup = () => {
            document.removeEventListener('visibilitychange', handleVisibilityChange);
            if (adFailTimeout) clearTimeout(adFailTimeout);
            if (pushTimeout) clearTimeout(pushTimeout);
        };

        const handleVisibilityChange = async () => {
            if (document.visibilityState === 'visible') {
                const elapsedSeconds = (Date.now() - startTime) / 1000;

                if (elapsedSeconds >= 30) {
                    cleanup();
                    setIsLoading(true);
                    try {
                        const userRef = doc(db, 'users', authUser.uid);
                        const updatedCases = [...(unlockedCases || []), caseData.id];
                        const updatedAdUnlocks = (dailyAdUnlocks || 0) + 1;

                        await updateDoc(userRef, {
                            unlockedCases: updatedCases,
                            dailyAdUnlocks: updatedAdUnlocks
                        });

                        if (setUnlockedCases) setUnlockedCases(updatedCases);
                        if (setDailyAdUnlocks) setDailyAdUnlocks(updatedAdUnlocks);

                        showNotification("ARQUIVO DESBLOQUEADO", "O informante liberou o acesso ao arquivo permanentemente.", "success");
                        setSelectedCaseModal(null);
                    } catch (error) {
                        console.error(error);
                        showNotification("ERRO NO SISTEMA", "Falha de comunicação com o servidor.", "alert");
                    }
                    setIsLoading(false);
                } else {
                    // Grace period - 3.5s tolerance for app swapping (Shopee/Aliexpress opens and sends browser back to background)
                    adFailTimeout = setTimeout(() => {
                        if (document.visibilityState === 'visible') {
                            cleanup();
                            showNotification("CONEXÃO PERDIDA", `Você retornou muito cedo (${Math.floor(elapsedSeconds)}s). A transação não foi confirmada.`, "alert");
                        }
                    }, 3500);
                }
            } else {
                if (adFailTimeout) {
                    clearTimeout(adFailTimeout);
                    adFailTimeout = null;
                }
            }
        };

        document.addEventListener('visibilitychange', handleVisibilityChange);
    };

    // --- MONETIZATION & ENERGY LOGIC ---
    const AD_LINK = 'https://omg10.com/4/10670491';
    const MAX_LUPAS_PASSIVAS = 15;


    const triggerAdWatch = () => {
        if (adUsesToday >= 5) {
            showNotification("NEGADO", "Sinal esgotado por hoje. Limite diário: 5.", 'alert');
            return;
        }

        if ((adLupas || 0) >= 13) {
            showNotification("CAPACIDADE MÁXIMA", "O banco de dados da carteira de anúncios está cheio. O Limite é 15.", 'alert');
            return;
        }

        const adWindow = window.open(AD_LINK, '_blank');

        if (!adWindow || adWindow.closed || typeof adWindow.closed === 'undefined') {
            showNotification("SINAL BLOQUEADO", "Desative seu protetor de conexão (Adblock) para que possamos interceptar a frequência.", 'alert');
            return;
        }

        const startTime = Date.now();
        let adFailTimeout: any;

        const targetSeconds = 5;
        const pushTimeout = setTimeout(() => {
            if (document.visibilityState !== 'visible' && 'serviceWorker' in navigator) {
                navigator.serviceWorker.ready.then(registration => {
                    registration.showNotification("Sinal Descriptografado!", {
                        body: "A energia foi restabelecida. Você já pode retornar à agência.",
                        icon: "/logo.png", badge: "/logo.png"
                    });
                }).catch(() => { });
            }
        }, targetSeconds * 1000);

        const cleanup = () => {
            document.removeEventListener('visibilitychange', handleVisibilityChange);
            if (adFailTimeout) clearTimeout(adFailTimeout);
            if (pushTimeout) clearTimeout(pushTimeout);
        };

        const handleVisibilityChange = async () => {
            if (document.visibilityState === 'visible') {
                const elapsedSeconds = (Date.now() - startTime) / 1000;

                if (elapsedSeconds >= 5) {
                    cleanup();
                    // SUCCESS
                    if (adUsesToday < 5 && authUser && db) {
                        try {
                            const newAdLupas = (adLupas || 0) + 3;
                            const userRef = doc(db, 'users', authUser.uid);
                            await updateDoc(userRef, { adLupas: newAdLupas, adUsesToday: adUsesToday + 1 });

                            if (setAdLupas) setAdLupas(newAdLupas);
                            if (setAdUsesToday) setAdUsesToday(adUsesToday + 1);

                            showNotification("INTERCEPTAÇÃO BEM SUCEDIDA", "Sinal descriptografado. +3 Lupas de Anúncios adicionadas.", 'success');
                        } catch (e) {
                            console.error("Failed to reward ad", e);
                        }
                    }
                } else {
                    // Grace period - 3.5s tolerance for app swapping
                    adFailTimeout = setTimeout(() => {
                        if (document.visibilityState === 'visible') {
                            cleanup();
                            showNotification("CONEXÃO INTERROMPIDA", "Tempo insuficiente para descriptografar os dados. Mínimo 5s.", 'alert');
                        }
                    }, 3500);
                }
            } else {
                if (adFailTimeout) {
                    clearTimeout(adFailTimeout);
                    adFailTimeout = null;
                }
            }
        };

        document.addEventListener('visibilitychange', handleVisibilityChange);
    };

    const handleLogout = async () => {
        try {
            await firebaseLogout();
            // Redirect will be handled by RequireAuth or user effect
        } catch (error) {
            console.error("Logout failed:", error);
        }
    };

    const handleCompleteTutorial = (tutorialId: string) => {
        setGameState(prev => ({
            ...prev,
            viewedTutorials: [...(prev.viewedTutorials || []), tutorialId]
        }));
        if (db && authUser) {
            const userRef = doc(db, 'users', authUser.uid);
            updateDoc(userRef, { viewedTutorials: [...(gameState.viewedTutorials || []), tutorialId] });
        }
    };

    const handleRedeemCode = async () => {
        if (!redeemCode.trim() || !authUser || !db) return;
        setIsRedeeming(true);

        try {
            // Find code
            const q = query(collection(db, 'redeem_codes'), where('code', '==', redeemCode.trim().toUpperCase()), where('status', '==', 'active'));
            const querySnapshot = await getDocs(q);

            if (querySnapshot.empty) {
                alert("Código inválido, expirado ou já utilizado.");
                setIsRedeeming(false);
                return;
            }

            const codeDoc = querySnapshot.docs[0];
            const codeData = codeDoc.data();
            const userRef = doc(db, 'users', authUser.uid);
            const userSnap = await (await import('firebase/firestore')).getDoc(userRef);

            if (codeData.rewardType === 'lupas') {
                let currentExtraLupas = 0;
                if (userSnap.exists()) {
                    currentExtraLupas = userSnap.data().extraLupas || 0;
                }
                const newExtraLupas = currentExtraLupas + (codeData.rewardValue || 0);

                await updateDoc(userRef, {
                    extraLupas: newExtraLupas
                });

                alert(`Sucesso! Você resgatou +${codeData.rewardValue} Lupas 🔍.`);
            } else if (codeData.rewardType === 'unlock_case') {
                const caseId = codeData.rewardValue;
                let currentUnlocked: string[] = [];
                if (userSnap.exists()) {
                    currentUnlocked = userSnap.data().unlockedCases || [];
                }

                if (currentUnlocked.includes(caseId)) {
                    alert("Você já possui acesso a este Arquivo.");
                    setIsRedeeming(false);
                    return;
                }

                currentUnlocked.push(caseId);
                await updateDoc(userRef, { unlockedCases: currentUnlocked });
                alert("Operação autorizada! O Arquivo Especial de Temporada foi destrancado no seu perfil.");
            } else if (codeData.rewardType === 'keys') {
                let currentKeys = 0;
                if (userSnap.exists()) {
                    currentKeys = userSnap.data().archiveKeys || 0;
                }
                const newKeys = currentKeys + (codeData.rewardValue || 0);

                await updateDoc(userRef, {
                    archiveKeys: newKeys
                });

                alert(`Sucesso! Você resgatou +${codeData.rewardValue} Chaves de Arquivo 🔑.`);
            } else {
                // Calculate new expiration date
                let baseDate = new Date();

                if (userSnap.exists()) {
                    const userData = userSnap.data();
                    if (userData.role === 'subscriber' && userData.subscriptionExpiresAt && (userData.subscriptionExpiresAt.seconds * 1000) > Date.now()) {
                        baseDate = new Date(userData.subscriptionExpiresAt.seconds * 1000);
                    }
                }

                const daysToAdd = codeData.rewardValue || codeData.days || 1;
                baseDate.setDate(baseDate.getDate() + daysToAdd);

                // Update user
                await updateDoc(userRef, {
                    role: 'subscriber',
                    subscriptionExpiresAt: baseDate
                });

                alert(`Sucesso! Você ativou ${daysToAdd} dias de acesso VIP 👑. Reinicie o jogo se necessário.`);
            }

            // Invalidate code
            await updateDoc(doc(db, 'redeem_codes', codeDoc.id), {
                status: 'used',
                usedBy: authUser.email || authUser.uid,
                usedAt: new Date()
            });
            setRedeemCode('');
            setIsProfileOpen(false);

        } catch (error) {
            console.error("Erro ao resgatar código:", error);
            alert("Ocorreu um erro ao processar seu código.");
        } finally {
            setIsRedeeming(false);
        }
    };


    const isNight = () => {
        const hour = new Date(gameState.gameTime).getHours();
        return hour >= 22 || hour < 8;
    };

    const calculateDistance = (locA: Location, locB: Location) => {
        const dx = locA.x - locB.x;
        const dy = locA.y - locB.y;
        return Math.hypot(dx, dy); // Euclidean distance
    };

    const triggerTimeFeedback = (label: string) => {
        setTimeFeedback({ label, id: Date.now() });
        setTimeout(() => setTimeFeedback(null), 1500);
    };

    // Helper to sync global gameTime with active case time
    const updateGameTime = (newTime: number, caseId: string | null = gameState.selectedCaseId) => {
        setGameState(prev => ({
            ...prev,
            gameTime: newTime,
            activeCases: prev.activeCases.map(c => c.id === caseId ? { ...c, lastSavedTime: newTime } : c)
        }));
    };

    useEffect(() => {
        let interval: ReturnType<typeof setInterval>;
        if (gameState.travelStatus === 'moving' && gameState.travelArrivalTime) {
            interval = setInterval(() => {
                const now = Date.now();
                const diff = gameState.travelArrivalTime! - now;

                if (diff <= 0) {
                    setGameState(prev => {
                        const updatedCases = prev.activeCases.map(c => {
                            if (c.id === prev.selectedCaseId) {
                                const newVisited = new Set(c.visitedLocationIds);
                                if (prev.travelDestinationId) newVisited.add(prev.travelDestinationId);

                                return {
                                    ...c,
                                    currentLocationId: prev.travelDestinationId,
                                    visitedLocationIds: Array.from(newVisited) as string[],
                                    logs: [...c.logs, { timestamp: formatTimeLog(prev.gameTime), type: 'system', content: `Chegada confirmada.` }]
                                } as CaseData;
                            }
                            return c;
                        });

                        return {
                            ...prev,
                            status: GameStatus.INVESTIGATING,
                            activeCases: updatedCases,
                            travelStatus: 'idle',
                            travelDestinationId: null,
                            travelArrivalTime: null,
                            travelTotalDuration: null,
                        };
                    });
                    setTimeLeft(0);
                    clearInterval(interval);
                    setActiveTab('investigation');
                    setInteractionText("");
                } else {
                    setTimeLeft(diff);
                }
            }, 50);
        }
        return () => clearInterval(interval);
    }, [gameState.travelStatus, gameState.travelArrivalTime]);

    useEffect(() => {
        let interval: ReturnType<typeof setInterval>;
        if (gameState.nextActionTime) {
            interval = setInterval(() => {
                const now = Date.now();
                const diff = gameState.nextActionTime! - now;
                if (diff <= 0) {
                    setCooldownLeft(0);
                    setGameState(prev => ({ ...prev, nextActionTime: null }));
                    clearInterval(interval);
                } else {
                    setCooldownLeft(Math.ceil(diff / 1000));
                }
            }, 1000);
        }
        return () => clearInterval(interval);
    }, [gameState.nextActionTime]);

    const activeCase = gameState.activeCases.find(c => c.id === gameState.selectedCaseId) || null;

    // Calculate current player location object for Physics
    const playerCurrentLocation = activeCase?.currentLocationId === 'HQ'
        ? HQ_LOCATION
        : activeCase?.locations.find(l => l.id === activeCase.currentLocationId) || HQ_LOCATION;

    const [lockedCaseModal, setLockedCaseModal] = useState<CaseData | null>(null);
    const showNotification = (title: string, message: string, type: 'info' | 'success' | 'alert' = 'info') => {
        const id = Date.now();
        setNotifications(prev => [...prev, { id, title, message, type }]);
        setTimeout(() => {
            setNotifications(prev => prev.filter(n => n.id !== id));
        }, 5000);
    };

    const addLog = (type: LogEntry['type'], content: string, details?: string) => {
        if (!activeCase) return;
        setGameState(prev => ({
            ...prev,
            activeCases: prev.activeCases.map(c => c.id === activeCase.id ? {
                ...c,
                logs: [...c.logs, { timestamp: formatTimeLog(prev.gameTime), type, content, details }]
            } : c)
        }));
    };

    const createCase = async (isDaily: boolean) => {
        setIsLoading(true);
        try {
            // Check if tutorial already exists for daily button
            if (isDaily) {
                const existing = gameState.activeCases.find(c => c.id === 'tutorial-case-001');
                if (existing) {
                    selectCase(existing.id);
                    return;
                }
            }

            const newCaseRaw = isDaily ? await getTutorialCase() : await generateCase();
            
            // Normalize and Ensure all properties exist
            const newCase: CaseData = {
                ...newCaseRaw,
                scoutedLocationIds: newCaseRaw.scoutedLocationIds || ['HQ'],
                visitedLocationIds: newCaseRaw.visitedLocationIds || ['HQ'],
                metCharacterIds: newCaseRaw.metCharacterIds || [],
                inventory: newCaseRaw.inventory || [],
                knownCharacterNames: newCaseRaw.knownCharacterNames || {},
                logs: newCaseRaw.logs || []
            };
    
            setGameState(prev => ({
                ...prev,
                activeCases: [...prev.activeCases, newCase],
                selectedCaseId: newCase.id,
                status: GameStatus.INVESTIGATING,
                dashboardView: 'main',
                gameTime: newCase.lastSavedTime
            }));
            setActiveTab('investigation');
            setInteractionText('');
            showNotification("DOSSIÊ ABERTO", newCase.title, 'success');
        } catch (e) {
            showNotification("ERRO", "Falha ao acessar os arquivos.", 'alert');
        } finally {
            setIsLoading(false);
        }
    };

    const createAcmeCase = async () => {
        if (!process.env.VITE_API_KEY && !import.meta.env.VITE_API_KEY) {
            showNotification("ERRO NO SISTEMA", "Chave Gemini indispónivel. Contate o suporte.", 'alert');
            return;
        }

        if ((dailyAcmeCreations || 0) >= 5) {
            showNotification("LIMITE ATINGIDO", "Você já excedeu sua cota diária de 5 casos gerados no Clube ACME.", "alert");
            return;
        }

        if (!acmeSettings.seed.trim()) {
            showNotification("DADOS INCOMPLETOS", "É obrigatório inserir uma Semente/Inspiração para gerar o caso.", "alert");
            return;
        }

        setIsAcmeGeneratorOpen(false);
        setIsLoading(true);

        try {
            const newCaseRaw = await generateCase(
                acmeSettings.difficulty,
                acmeSettings.duration,
                false,   // not special
                [acmeSettings.seed] // Custom Theme
            );

            // Normalize and Ensure all properties exist
            const newCase: CaseData = {
                ...newCaseRaw,
                scoutedLocationIds: newCaseRaw.scoutedLocationIds || ['HQ'],
                visitedLocationIds: newCaseRaw.visitedLocationIds || ['HQ'],
                metCharacterIds: newCaseRaw.metCharacterIds || [],
                inventory: newCaseRaw.inventory || [],
                knownCharacterNames: newCaseRaw.knownCharacterNames || {},
                logs: newCaseRaw.logs || []
            };

            if (db && authUser) {
                const userRef = doc(db, 'users', authUser.uid);
                const newAmount = (dailyAcmeCreations || 0) + 1;
                await updateDoc(userRef, { dailyAcmeCreations: newAmount });
                if (setDailyAcmeCreations) setDailyAcmeCreations(newAmount);
            }

            setGameState(prev => ({
                ...prev,
                activeCases: [...prev.activeCases, newCase],
                selectedCaseId: newCase.id,
                status: GameStatus.INVESTIGATING,
                dashboardView: 'main',
                gameTime: newCase.lastSavedTime
            }));
            setActiveTab('investigation');
            setInteractionText('');
            showNotification("NOVO CASO GERADO", newCase.title, 'success');

        } catch (e) {
            console.error("Erro do Generator: ", e);
            showNotification("ERRO NA GERAÇÃO", "O servidor sentiu fadiga e abortou. Tente novamente.", 'alert');
        } finally {
            setIsLoading(false);
        }
    };

    const startGlobalCase = (gCase: CaseData) => {
        const existingActive = gameState.activeCases.find(c => c.id === gCase.id);
        if (existingActive) {
            selectCase(existingActive.id);
            return;
        }

        const existingArchived = gameState.archivedCases.find(c => c.id === gCase.id);
        if (existingArchived) {
            showNotification("CASO ARQUIVADO", "Você já concluiu ou falhou neste caso.", 'info');
            return;
        }

        const hasPurchased = (unlockedCases || []).includes(gCase.id);
        const isVIP = role === 'subscriber' || role === 'admin';
        const isComingSoon = Boolean(gCase.scheduledDate && gCase.scheduledDate > Date.now());

        if (!hasPurchased && !isVIP && gCase.id !== 'tutorial-case-001') {
            setSelectedCaseModal(gCase);
            showNotification("ACESSO REVOGADO", "Renove sua credencial VIP ou use um Mandado de Busca para acessar os arquivos da sua mesa.", "alert");
            return;
        }

        if (isComingSoon) {
            setSelectedCaseModal(gCase);
            return; // Prevent starting via direct link if coming soon
        }

        const newCase: CaseData = {
            ...gCase,
            status: 'active',
            lastSavedTime: new Date(START_DATE_ISO).getTime(),
            currentLocationId: 'HQ',
            scoutedLocationIds: gCase.scoutedLocationIds || ['HQ'],
            visitedLocationIds: gCase.visitedLocationIds || [],
            metCharacterIds: gCase.metCharacterIds || [],
            discoveredLocationIds: gCase.discoveredLocationIds || ['HQ', gCase.locations?.find((l: any) => l.isCrimeScene)?.id || gCase.locations?.[0]?.id],
            logs: gCase.logs || [],
            knownCharacterNames: {}
        };

        setGameState(prev => ({
            ...prev,
            activeCases: [...prev.activeCases, newCase],
            selectedCaseId: newCase.id,
            status: GameStatus.INVESTIGATING,
            dashboardView: 'main',
            gameTime: newCase.lastSavedTime
        }));
        setActiveTab('investigation');
        setInteractionText('');
        showNotification("DOSSIÊ ABERTO", newCase.title, 'success');
    };

    const selectCase = (caseId: string) => {
        const targetCase = gameState.activeCases.find(c => c.id === caseId);
        if (!targetCase) return;

        // Resume from last saved time
        setGameState(prev => ({
            ...prev,
            selectedCaseId: caseId,
            status: GameStatus.INVESTIGATING,
            gameTime: targetCase.lastSavedTime
        }));
        setActiveTab('investigation');
        setInteractionText('');
    };

    const archiveCase = (caseId: string, result: 'solved' | 'failed') => {
        setGameState(prev => {
            const caseToArchive = prev.activeCases.find(c => c.id === caseId);
            if (!caseToArchive) return prev;

            return {
                ...prev,
                activeCases: prev.activeCases.filter(c => c.id !== caseId),
                archivedCases: [...prev.archivedCases, { ...caseToArchive, status: result }],
                selectedCaseId: null,
                status: GameStatus.DASHBOARD
            };
        });
    };

    const startTravel = (loc: Location, travelMinutes: number) => {
        if (!activeCase) return;

        // Physics Logic handled in LocationCard render, but we need to commit the time here
        // Distance was calculated in render time, now we apply the cost
        const addedTimeMs = travelMinutes * 60 * 1000;
        const newTime = gameState.gameTime + addedTimeMs;

        const visualDurationMs = 2000;
        const visualArrivalTime = Date.now() + visualDurationMs;

        triggerTimeFeedback(`+${travelMinutes}min`);

        // Update both global and case time
        setGameState(prev => ({
            ...prev,
            travelStatus: 'moving',
            travelDestinationId: loc.id,
            travelArrivalTime: visualArrivalTime,
            travelTotalDuration: visualDurationMs,
            gameTime: newTime,
            activeCases: prev.activeCases.map(c => c.id === activeCase.id ? { ...c, lastSavedTime: newTime } : c)
        }));
        setActiveTab('investigation');
    };

    const skipTravel = () => {
        setGameState(prev => ({ ...prev, travelArrivalTime: Date.now() }));
    };

    const handleSleep = () => {
        const current = new Date(gameState.gameTime);
        const nextMorning = new Date(current);
        nextMorning.setDate(current.getDate() + 1);
        nextMorning.setHours(8, 0, 0, 0);

        const newTime = nextMorning.getTime();

        setGameState(prev => ({
            ...prev,
            gameTime: newTime,
            activeCases: prev.activeCases.map(c => c.id === prev.selectedCaseId ? {
                ...c,
                lastSavedTime: newTime,
                logs: [...c.logs, { timestamp: formatTimeLog(newTime), type: 'system', content: 'Você dormiu no escritório. Novo dia iniciado.' }]
            } : c)
        }));
        showNotification("DESCANSADO", "Novo dia iniciado às 08:00.", "success");
    };

    const handlePhoneCall = (charId: string) => {
        if (!activeCase) return;
        if (isNight()) return;

        const char = activeCase.characters.find(c => c.id === charId);
        if (!char) return;

        setIsLoading(true);
        setTimeout(() => {
            const success = Math.random() > 0.4;
            if (success) {
                const arrivalTime = gameState.gameTime + (Math.random() * 60 * 60 * 1000);
                const minutes = Math.ceil((arrivalTime - gameState.gameTime) / 60000);
                const costTime = gameState.gameTime + (5 * 60000);

                setGameState(prev => ({
                    ...prev,
                    gameTime: costTime,
                    activeCases: prev.activeCases.map(c => c.id === activeCase.id ? {
                        ...c,
                        lastSavedTime: costTime,
                        characters: c.characters.map(ch => ch.id === charId ? { ...ch, arrivalAtHQ: arrivalTime, locationId: 'HQ' } : ch)
                    } : c)
                }));
                triggerTimeFeedback("+5min");
                setInteractionText(`(Telefone) ${char.name}: "Alô? Detetive... tudo bem. Vou para o QG prestar depoimento. Chego em ${minutes} minutos."`);
                showNotification("AGENDADO", `${char.name} a caminho do QG.`, 'success');
            } else {
                const costTime = gameState.gameTime + (2 * 60000);
                setGameState(prev => ({
                    ...prev,
                    gameTime: costTime,
                    activeCases: prev.activeCases.map(c => c.id === activeCase.id ? { ...c, lastSavedTime: costTime } : c)
                }));
                triggerTimeFeedback("+2min");
                setInteractionText(`(Telefone) *Caixa Postal*... Ninguém atendeu.`);
                showNotification("SEM RESPOSTA", "Tente ligar novamente mais tarde.", 'alert');
            }
            setIsLoading(false);
        }, 1000);
    };

    const investigateLocation = async (loc: Location) => {
        if (!activeCase) return;
        if (isNight()) return;

        updateInteractionDate();

        const COST_MINUTES = 60;
        const newTime = gameState.gameTime + (COST_MINUTES * 60000);

        let result = loc.evidence || "Você vasculha o local mas não encontra nada de relevante à primeira vista.";
        let unlockedLocs: string[] = [];
        let unlockedChars: string[] = [];

        let newInventoryItems: Clue[] = [];

        if (loc.clues && loc.clues.length > 0) {
            result = loc.clues.map(c => c.description).join("\n\n");
            loc.clues.forEach(c => {
                if (c.unlocksLocations && !c.isInspectable) unlockedLocs.push(...c.unlocksLocations);
                if (c.unlocksCharacters && !c.isInspectable) unlockedChars.push(...c.unlocksCharacters);
                if (c.isInspectable) {
                    // Previne duplicatas caso o local seja vasculhado multiplas vezes
                    if (!activeCase.inventory.find(i => i.description === c.description)) {
                        newInventoryItems.push({
                            id: `item_${Date.now()}_${Math.random()}`,
                            description: c.description,
                            name: "Item Encontrado",
                            foundAt: loc.name,
                            isInspectable: true,
                            inspectResult: c.inspectResult
                        });
                    }
                }
            });
        }

        setInteractionText(result);

        const alreadyLogged = activeCase.logs.some(l => l.type === 'clue' && l.content === `Investigação: ${loc.name}`);
        if (!alreadyLogged) {
            addLog('clue', `Investigação: ${loc.name}`, result);
        }

        const newDiscovered = new Set(activeCase.discoveredLocationIds);
        let updated = false;

        // Fallback string matching for old cases
        activeCase.locations.forEach(l => {
            const resultLower = result.toLowerCase();
            const locNameLower = l.name.toLowerCase();
            let matched = false;

            if (resultLower.includes(locNameLower)) {
                matched = true;
            } else {
                const words = locNameLower.split(/[\s'"]+/).filter(w => w.length > 4);
                if (words.some(w => resultLower.includes(w))) {
                    matched = true;
                }
            }

            if (matched && !newDiscovered.has(l.id)) {
                newDiscovered.add(l.id);
                updated = true;
                showNotification("NOVO LOCAL", l.name, 'success');
            }
        });

        // Explicit new locations unlocked
        unlockedLocs.forEach(locId => {
            if (!newDiscovered.has(locId)) {
                newDiscovered.add(locId);
                updated = true;
                const revealedLoc = activeCase.locations.find(l => l.id === locId);
                if (revealedLoc) showNotification("NOVO LOCAL", revealedLoc.name, 'success');
            }
        });

        // Update scouted locations
        const newScouted = new Set(activeCase.scoutedLocationIds);
        if (!newScouted.has(loc.id)) {
            newScouted.add(loc.id);
            showNotification("ÁREA SEGURA", "Pessoas no local identificadas.", 'info');
        }

        let newMet = false;
        const currentMet = new Set(activeCase.metCharacterIds);

        // Unlocking characters present exactly at the scouted location
        activeCase.characters
            .filter(c => c.locationId === loc.id)
            .forEach(c => {
                if (!currentMet.has(c.id)) {
                    currentMet.add(c.id);
                    newMet = true;
                }
            });

        // Unlocking characters globally referenced by clues explicitly
        unlockedChars.forEach(charId => {
            if (!currentMet.has(charId)) {
                currentMet.add(charId);
                newMet = true;
                const revealedChar = activeCase.characters.find(c => c.id === charId);
                if (revealedChar) showNotification("NOVA IDENTIDADE", revealedChar.name, 'info');
            }
        });

        triggerTimeFeedback("+1h");

        setGameState(prev => ({
            ...prev,
            gameTime: newTime,
            activeCases: prev.activeCases.map(c => c.id === activeCase.id ? {
                ...c,
                lastSavedTime: newTime,
                discoveredLocationIds: Array.from(newDiscovered) as string[],
                scoutedLocationIds: Array.from(newScouted) as string[],
                metCharacterIds: Array.from(currentMet) as string[],
                inventory: [...c.inventory, ...newInventoryItems]
            } : c)
        }));
    };

    const handleInspectItem = (itemInfo: Clue) => {
        if (!activeCase || isNight()) return;

        const COST_MINUTES = 30;
        const newTime = gameState.gameTime + (COST_MINUTES * 60000);

        const result = itemInfo.inspectResult?.story || "O item parece não ter nada de especial após uma inspeção mais minuciosa.";
        
        let unlockedLocs = itemInfo.inspectResult?.unlocksLocations || [];
        let unlockedChars = itemInfo.inspectResult?.unlocksCharacters || [];

        setInteractionText(result);
        addLog('clue', `Inspeção: ${itemInfo.name || 'Item'}`, result);

        const newDiscovered = new Set(activeCase.discoveredLocationIds);
        let updatedLocs = false;

        unlockedLocs.forEach(locId => {
            if (!newDiscovered.has(locId)) {
                newDiscovered.add(locId);
                updatedLocs = true;
                const revealedLoc = activeCase.locations.find(l => l.id === locId);
                if (revealedLoc) showNotification("NOVO LOCAL", revealedLoc.name, 'success');
            }
        });

        let newMet = false;
        const currentMet = new Set(activeCase.metCharacterIds);
        unlockedChars.forEach(charId => {
            if (!currentMet.has(charId)) {
                currentMet.add(charId);
                newMet = true;
                const revealedChar = activeCase.characters.find(c => c.id === charId);
                if (revealedChar) showNotification("NOVA IDENTIDADE", revealedChar.name, 'info');
            }
        });

        triggerTimeFeedback("+30min");

        setGameState(prev => ({
            ...prev,
            gameTime: newTime,
            activeCases: prev.activeCases.map(c => c.id === activeCase.id ? {
                ...c,
                lastSavedTime: newTime,
                discoveredLocationIds: Array.from(newDiscovered) as string[],
                metCharacterIds: Array.from(currentMet) as string[],
                // Update item as no longer inspectable to avoid re-inspection
                inventory: c.inventory.map(invItem => invItem.id === itemInfo.id ? { ...invItem, isInspectable: false } : invItem)
            } : c)
        }));
    };

    const handleInterrogation = async (charId: string, q: string) => {
        if (!activeCase) return;
        if (isNight()) return;

        updateInteractionDate();

        const char = activeCase.characters.find(c => c.id === charId);
        if (!char) return;

        // ENERGY CHECK
        if (role !== 'subscriber' && role !== 'admin') {
            if (totalLupas <= 0) {
                setInteractionText("Detetive, você está sem Energia (Lupas). Descanse até amanhã, assista a um anúncio ou ative a Credencial VIP para continuar a investigação de forma livre.");
                showNotification("SEM LUPAS", "Energia esgotada.", 'alert');
                return;
            }
        }

        // TIME COST CALCULATION (Dynamic based on query length: 5 to 15 mins)
        // Avg 5 chars per word. 1 minute per 15 words ~= 75 chars.
        // Let's make it min 5, max 15. Every 30 chars adds 1 min.
        const baseMin = 5;
        const extraMin = Math.floor(q.length / 30);
        const COST_MINUTES = Math.min(15, baseMin + extraMin);
        const newTime = gameState.gameTime + (COST_MINUTES * 60000);

        const currentLoc = activeCase.locations.find(l => l.id === gameState.dashboardView);

        if (currentLoc?.id === 'loc_hq' && char.arrivalAtHQ && gameState.gameTime < char.arrivalAtHQ) {
            const minLeft = Math.ceil((char.arrivalAtHQ - gameState.gameTime) / 60000);
            setInteractionText(`A recepção informa que ${char.name} ainda não chegou. Aguarde ${minLeft} minutos.`);
            return;
        }

        setIsLoading(true);
        try {
            // LUPA DEDUCTION LOGIC
            if (role !== 'subscriber' && role !== 'admin') {
                const userRef = doc(db, 'users', authUser.uid);
                let currentDaily = dailyLupas || 0;
                let currentAdLupas = adLupas || 0;
                let currentExtra = extraLupas || 0;

                if (currentDaily > 0) {
                    currentDaily -= 1;
                    await updateDoc(userRef, { dailyLupas: currentDaily });
                    if (setDailyLupas) setDailyLupas(currentDaily);
                } else if (currentAdLupas > 0) {
                    currentAdLupas -= 1;
                    await updateDoc(userRef, { adLupas: currentAdLupas });
                    if (setAdLupas) setAdLupas(currentAdLupas);
                } else if (currentExtra > 0) {
                    currentExtra -= 1;
                    await updateDoc(userRef, { extraLupas: currentExtra });
                    // Provide optimistic UI update (Requires extending hook to export setExtraLupas if needed, currently skipping and fetching automatically in next cycle/hook trigger)
                }
            }

            const currentLocStr = currentLoc ? currentLoc.name : (gameState.dashboardView === 'hq' ? 'QG' : 'Investigação Geral');
            const contextObj = {
                solution: activeCase.solution,
                currentLocation: currentLocStr,
                locations: [HQ_LOCATION, ...activeCase.locations],
                knownClues: activeCase.logs.filter(l => l.type === 'clue').map(l => l.content)
            };

            const res = await interrogateCharacter(
                char.name,
                char.role,
                char.description,
                q,
                JSON.stringify(contextObj)
            );

            setInteractionText(`${char.name}: "${res}"`);
            addLog('dialogue', `Você: ${q}`, res);
            setInputQuery('');

            let stateUpdate: Partial<CaseData> = {};

            // Reveal Name on first interaction
            if (!activeCase.knownCharacterNames[char.id]) {
                stateUpdate.knownCharacterNames = { ...activeCase.knownCharacterNames, [char.id]: true };
                showNotification("IDENTIDADE", `Nome confirmado: ${char.name}`, 'success');
            }

            if (!activeCase.metCharacterIds.includes(char.id)) {
                stateUpdate.metCharacterIds = [...activeCase.metCharacterIds, char.id];
            }

            const currentDiscovered = new Set(activeCase.discoveredLocationIds);
            let newLocFound = false;

            activeCase.locations.forEach(loc => {
                if (!currentDiscovered.has(loc.id) && res.toLowerCase().includes(loc.name.toLowerCase())) {
                    currentDiscovered.add(loc.id);
                    newLocFound = true;
                    showNotification("NOVO LOCAL", `Acesso liberado: ${loc.name}`, 'success');
                }
            });

            if (newLocFound) {
                stateUpdate.discoveredLocationIds = Array.from(currentDiscovered) as string[];
            }

            triggerTimeFeedback("+30min");

            setGameState(prev => ({
                ...prev,
                gameTime: newTime,
                nextActionTime: Date.now() + (INTERROGATION_COOLDOWN_SEC * 1000),
                activeCases: prev.activeCases.map(c => c.id === activeCase.id ? {
                    ...c,
                    lastSavedTime: newTime,
                    ...stateUpdate
                } : c)
            }));

            setCooldownLeft(INTERROGATION_COOLDOWN_SEC);
        } catch (e) { setInteractionText("Sem resposta."); }
        finally { setIsLoading(false); }
    };

    const handleArrest = async (suspectId: string) => {
        if (!activeCase) return;

        let repPointsGained = 0;
        const isPublicCase = activeCase.publishStatus === 'published';

        if (suspectId === activeCase.solution.culpritId) {
            archiveCase(activeCase.id, 'solved');

            if (isPublicCase && authUser && db) {
                const currentRep = reputacao || 0;
                const newRep = currentRep + 20;
                repPointsGained = 20;

                try {
                    const userRef = doc(db, 'users', authUser.uid);
                    await updateDoc(userRef, { reputacao: newRep });
                    if (setReputacao) setReputacao(newRep);

                    const oldRank = getRankInfo(currentRep);
                    const newRank = getRankInfo(newRep);

                    if (newRank.level > oldRank.level && !(patentesRecompensadas || []).includes(newRank.level)) {
                        let rewardMsg = "";
                        try {
                            const rankSnap = await getDoc(doc(db, 'settings', 'ranks'));
                            if (rankSnap.exists() && rankSnap.data().rewards) {
                                const rewards = rankSnap.data().rewards;
                                const reward = rewards[newRank.level];

                                if (reward && reward.rewardAmount > 0) {
                                    const amount = reward.rewardAmount;
                                    const type = reward.rewardType;
                                    let updateData: any = {
                                        patentesRecompensadas: [...(patentesRecompensadas || []), newRank.level]
                                    };

                                    if (type === 'lupas') {
                                        updateData.extraLupas = (extraLupas || 0) + amount;
                                        rewardMsg = `+ ${amount} Lupas Extras`;
                                    } else if (type === 'keys') {
                                        const newKeys = (archiveKeys || 0) + amount;
                                        updateData.archiveKeys = newKeys;
                                        if (setArchiveKeys) setArchiveKeys(newKeys);
                                        rewardMsg = `+ ${amount} Chaves de Arquivo`;
                                    } else if (type === 'vip_days') {
                                        const currentExpiry = vipExpiresAt ? new Date(vipExpiresAt).getTime() : Date.now();
                                        const newExpiry = new Date(currentExpiry + (amount * 24 * 60 * 60 * 1000));
                                        updateData.vipExpiresAt = newExpiry.toISOString();
                                        updateData.role = 'subscriber';
                                        rewardMsg = `+ ${amount} Dias VIP`;
                                    }

                                    await updateDoc(userRef, updateData);
                                    if (setPatentesRecompensadas) setPatentesRecompensadas(updateData.patentesRecompensadas);
                                }
                            }
                        } catch (e) {
                            console.error("Erro resgatando recompensas de patente:", e);
                        }

                        setPromotionModal({ newRankName: newRank.name, rewardMsg });
                    } else {
                        showNotification("CASO RESOLVIDO", "Culpado preso com sucesso! +20 Pontos de Reputação.", 'success');
                    }
                } catch (e) {
                    console.error("Erro ao atualizar reputação:", e);
                    showNotification("CASO RESOLVIDO", "Culpado preso com sucesso!", 'success');
                }
            } else {
                showNotification("CASO RESOLVIDO", "Culpado preso com sucesso! (Treinamento/Clube ACME não conta Reputação).", 'success');
            }
        } else {
            archiveCase(activeCase.id, 'failed');

            if (isPublicCase && authUser && db) {
                const currentRep = reputacao || 0;
                const newRep = Math.max(0, currentRep - 10);

                try {
                    const userRef = doc(db, 'users', authUser.uid);
                    await updateDoc(userRef, { reputacao: newRep });
                    if (setReputacao) setReputacao(newRep);

                    const oldRank = getRankInfo(currentRep);
                    const newRank = getRankInfo(newRep);

                    setCorregedoriaModal({
                        repLost: 10,
                        newRep: newRep,
                        rankName: newRank.level < oldRank.level ? newRank.name : undefined,
                        downranked: newRank.level < oldRank.level
                    });

                } catch (e) {
                    console.error(e);
                    showNotification("ERRO FATAL", "Inocente preso. Caso encerrado.", 'alert');
                }
            } else {
                showNotification("ERRO FATAL", "Inocente preso. Caso encerrado.", 'alert');
            }
        }
        setArrestModalOpen(null);
    };

    // --- RENDERIZADORES DE NAVEGAÇÃO ---

    // --- RENDERIZADORES DE NAVEGAÇÃO ---

    // 1. Menu Principal
    const renderDashboardMain = () => {
        // Hydrate the 'Case of the Day' (Card A) with Global Data so images load
        const mostRecentActiveCase = [...gameState.activeCases].sort((a, b) => (b.lastSavedTime || 0) - (a.lastSavedTime || 0))[0];
        const todayCaseSource = mostRecentActiveCase || staticTutorial;

        let todayCase = null;
        if (todayCaseSource) {
            const globalC = globalCases.find(g => g.id === todayCaseSource.id) || (staticTutorial?.id === todayCaseSource.id ? staticTutorial : null);
            todayCase = {
                ...todayCaseSource,
                thumbImageUrl: globalC?.thumbImageUrl || todayCaseSource.thumbImageUrl,
                heroImageUrl: globalC?.heroImageUrl || todayCaseSource.heroImageUrl,
                title: globalC?.title || todayCaseSource.title
            };
        }

        const hasActiveCases = gameState.activeCases.length > 0;

        return (
            <div className="flex flex-col md:flex-row gap-8 h-full items-center justify-center p-8 animate-in fade-in duration-500 max-w-6xl mx-auto">
                {/* Card A: Case of the Day (Tutorial ou Mais Recente) */}
                <div className="group relative w-full md:w-1/2 max-w-md aspect-[4/5] bg-[#1a1a1a] hover:border-noir-accent transition-all duration-300 flex flex-col cursor-pointer overflow-hidden rounded-xl shadow-2xl" onClick={() => setGameState(prev => ({ ...prev, dashboardView: 'daily_list' }))}>

                    {/* Background Image (Hero ou Thumb do todayCase) */}
                    {todayCase && (
                        <>
                            <img src={todayCase.heroImageUrl || todayCase.thumbImageUrl || `https://picsum.photos/seed/${todayCase.id}_hero/500/600`} alt={todayCase.title} className="absolute inset-0 w-full h-full object-cover opacity-40 group-hover:opacity-60 transition-opacity duration-300" />
                            <div className="absolute inset-0 bg-gradient-to-t from-black via-black/60 to-transparent z-0"></div>
                        </>
                    )}

                    <div className="absolute top-0 left-0 bg-noir-800/80 backdrop-blur text-noir-muted text-[10px] font-mono px-3 py-1 border-br rounded-br z-20">ARQUIVO PÚBLICO</div>

                    {
                        todayCase && gameState.activeCases.find(c => c.id === todayCase.id) && (
                            <div className="absolute top-4 right-4 bg-amber-500 text-black text-[10px] font-bold px-2 py-1 rounded shadow-lg animate-pulse z-20">
                                EM ANDAMENTO: {getDayLabel(todayCase.lastSavedTime)}
                            </div>
                        )
                    }

                    <div className="flex-1 flex flex-col items-center justify-center text-center z-10 p-6 relative">
                        <FolderOpen className={`w-20 h-20 transition-colors mb-6 stroke-1 drop-shadow-lg ${todayCase ? 'text-amber-500' : 'text-noir-500 group-hover:text-noir-accent'}`} />
                        <h3 className="text-3xl md:text-4xl font-black text-white mb-2 tracking-tighter drop-shadow-lg">CASOS PÚBLICOS</h3>
                        <p className="text-gray-300 text-sm max-w-xs leading-relaxed drop-shadow-md">
                            Casos e missões curados pelo departamento.
                        </p>
                        {todayCase && gameState.activeCases.find(c => c.id === todayCase.id) && (
                            <p className="text-amber-500 font-bold text-sm font-mono mt-4 bg-black/50 px-3 py-1 rounded-full border border-amber-900/50 backdrop-blur-sm">
                                {todayCase.title}
                            </p>
                        )}
                    </div>
                    <div className={`mt-auto w-full py-4 text-white group-hover:text-black font-bold text-center uppercase tracking-widest transition-colors z-20 ${todayCase ? 'bg-amber-700 hover:bg-amber-500' : 'bg-noir-800 hover:bg-noir-accent'}`}>
                        {todayCase && gameState.activeCases.find(c => c.id === todayCase.id) ? 'CONTINUAR' : 'VER ARQUIVOS'}
                    </div>
                </div >

                {/* Card B: ACME Club */}
                < div
                    className="group relative w-full md:w-1/2 max-w-md aspect-[4/5] bg-gradient-to-br from-amber-950/20 to-black border-2 border-amber-900/50 hover:border-amber-500 transition-all duration-300 flex flex-col p-6 cursor-pointer overflow-hidden rounded-xl shadow-2xl"
                    onClick={() => setGameState(prev => ({ ...prev, dashboardView: 'acme_club' }))}
                >
                    <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMjAiIGhlaWdodD0iMjAiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+PGNpcmNsZSBjeD0iMiIgY3k9IjIiIHI9IjEiIGZpbGw9IiNmNTk1NTUiIGZpbGwtb3BhY2l0eT0iMC4wNSIvPjwvc3ZnPg==')] opacity-30 pointer-events-none"></div>
                    <div className="absolute top-0 right-0 bg-amber-900/80 text-amber-200 text-[10px] font-bold font-mono px-3 py-1 flex items-center gap-1 rounded-bl shadow"><Star className="w-3 h-3 fill-current" /> PREMIUM</div>

                    <div className="flex-1 flex flex-col items-center justify-center text-center z-10">
                        <div className="relative">
                            <Zap className="w-24 h-24 text-amber-700 group-hover:text-amber-400 transition-colors mb-6 stroke-1" />
                            <div className="absolute -top-2 -right-2 animate-ping w-3 h-3 bg-amber-500 rounded-full opacity-0 group-hover:opacity-50"></div>
                        </div>
                        <h3 className="text-3xl font-mono font-bold text-amber-500 mb-2 tracking-tighter">CLUBE ACME</h3>
                        <p className="text-amber-900/80 group-hover:text-amber-700 text-sm max-w-xs leading-relaxed font-bold transition-colors">
                            Acesso a arquivos confidenciais e missões geradas por IA.
                        </p>
                    </div>
                    <div className="mt-auto w-full py-4 bg-amber-900/30 group-hover:bg-amber-500 text-amber-600 group-hover:text-black font-bold text-center uppercase tracking-widest transition-colors rounded border border-amber-900/50 flex items-center justify-center gap-2">
                        ACESSAR ÁREA RESTRITA
                    </div>
                </div >

                {/* Loja ACME Card */}
                < div
                    onClick={() => setIsStoreOpen(true)}
                    className="group relative w-full md:w-1/2 max-w-md aspect-[4/5] bg-[#1a1a1a] border border-[#2a2a2a] hover:border-green-600/50 transition-all duration-500 flex flex-col p-6 cursor-pointer overflow-hidden rounded-xl hover:shadow-2xl hover:shadow-green-900/20"
                >
                    <div className="absolute top-0 right-0 w-64 h-64 bg-green-500/5 rounded-full blur-3xl -mr-20 -mt-20 group-hover:bg-green-500/10 transition-colors"></div>
                    <div className="relative z-10 flex flex-col h-full">
                        <div className="relative">
                            <ShoppingCart className="w-24 h-24 text-green-800 group-hover:text-green-500 transition-colors mb-6 stroke-1" />
                            <div className="absolute -top-2 -right-2 w-3 h-3 bg-green-500 rounded-full group-hover:animate-ping opacity-0 group-hover:opacity-100"></div>
                        </div>
                        <h3 className="text-3xl font-mono font-bold text-green-500 mb-2 tracking-tighter">SUPRIMENTOS</h3>
                        <p className="text-green-900/80 group-hover:text-green-700 text-sm max-w-xs leading-relaxed font-bold transition-colors">
                            Adquira lupas extras, assine a credencial VIP e desbloqueie arquivos.
                        </p>
                    </div>
                    <div className="mt-auto w-full py-4 bg-green-900/20 group-hover:bg-green-500 text-green-600 group-hover:text-black font-bold text-center uppercase tracking-widest transition-colors rounded border border-green-900/30 flex items-center justify-center gap-2">
                        ACESSAR LOJA ACME
                    </div>
                </div >
            </div >
        )
    };

    // 2. Lista de Casos Diários (Estilo Netflix)
    const renderDailyList = () => {
        const isVIP = role === 'subscriber' || role === 'admin';
        const tutorialCase = gameState.activeCases.find(c => c.id === 'tutorial-case-001') || staticTutorial;
        const activeOrArchivedIds = new Set([...gameState.activeCases.map(c => c.id), ...gameState.archivedCases.map(c => c.id)]);
        const unseenCases = globalCases.filter(c => c.id !== 'tutorial-case-001' && !activeOrArchivedIds.has(c.id));

        // Hero Slider Logic (Top 5 Unseen Cases)
        const featuredCases = unseenCases.slice(0, 5);
        const dailyFeaturedSource = featuredCases.length > 0 ? featuredCases[heroIndex % featuredCases.length] : (gameState.activeCases.length > 0 ? gameState.activeCases[0] : null);

        let dailyFeatured = null;
        if (dailyFeaturedSource) {
            const globalC = globalCases.find(g => g.id === dailyFeaturedSource.id) || (staticTutorial?.id === dailyFeaturedSource.id ? staticTutorial : null);
            dailyFeatured = {
                ...dailyFeaturedSource,
                thumbImageUrl: globalC?.thumbImageUrl || dailyFeaturedSource.thumbImageUrl,
                heroImageUrl: globalC?.heroImageUrl || dailyFeaturedSource.heroImageUrl,
                title: globalC?.title || dailyFeaturedSource.title,
                description: globalC?.description || dailyFeaturedSource.description,
                category: globalC?.category || dailyFeaturedSource.category,
                difficulty: globalC?.difficulty || dailyFeaturedSource.difficulty,
                estimatedTime: globalC?.estimatedTime || dailyFeaturedSource.estimatedTime,
                isSpecialSeason: globalC?.isSpecialSeason !== undefined ? globalC.isSpecialSeason : dailyFeaturedSource.isSpecialSeason,
            };
        }

        // Categorize Cases (Only uncategorized go to Recentes)
        const categoryMap = new Map<string, CaseData[]>();
        const uncategorizedSettings: CaseData[] = [];

        unseenCases.forEach(c => {
            if (c.category && gameCategories.includes(c.category)) {
                if (!categoryMap.has(c.category)) categoryMap.set(c.category, []);
                categoryMap.get(c.category)!.push(c);
            } else {
                uncategorizedSettings.push(c);
            }
        });

        // Sort Map to ensure consistency with admin settings layout order if needed (or keep random/insert)
        const sortedCategoryRows = gameCategories.map(cat => ({
            catLabel: cat,
            cases: categoryMap.get(cat) || []
        })).filter(row => row.cases.length > 0);

        return (
            <div className="flex flex-col h-full bg-[#141414] overflow-y-auto pb-20 no-scrollbar relative w-full animate-in slide-in-from-right duration-500">
                <button onClick={() => setGameState(prev => ({ ...prev, dashboardView: 'main' }))} className="absolute top-4 left-4 z-50 p-2 bg-black/50 rounded-full hover:bg-black/80 transition-colors backdrop-blur-sm">
                    <ChevronLeft className="w-6 h-6 text-white" />
                </button>
                <div className="absolute top-4 right-4 z-50 flex items-center gap-4">
                    <button onClick={() => {
                        setAnnouncementModalOpen(true);
                        const ids = announcements.map(a => a.id!);
                        localStorage.setItem('chronos_read_announcements', JSON.stringify(ids));
                        setUnreadAnnouncementsCount(0);
                    }} className="relative text-white hover:text-amber-500 transition-colors bg-black/50 p-2 rounded-full backdrop-blur-sm">
                        <Bell className="w-5 h-5" />
                        {unreadAnnouncementsCount > 0 && <span className="absolute -top-1 -right-1 bg-red-600 text-white text-[10px] w-4 h-4 flex items-center justify-center rounded-full animate-pulse font-bold">{unreadAnnouncementsCount}</span>}
                    </button>
                    {UserBadge()}
                </div>
                {/* Hero Section (Featured Cases Carousel) */}
                <div className="relative w-full h-[60vh] min-h-[400px] flex-shrink-0 transition-all duration-700 ease-in-out">
                    <div className="absolute inset-0 bg-gradient-to-t from-[#141414] via-[#141414]/40 to-transparent z-10"></div>
                    {featuredCases.length > 0 && dailyFeatured && (
                        <img
                            key={dailyFeatured.id}
                            src={dailyFeatured.heroImageUrl || `https://picsum.photos/seed/${dailyFeatured.id}_hero/1000/600`}
                            className="absolute inset-0 w-full h-full object-cover opacity-60 animate-in fade-in zoom-in-95 duration-1000"
                            alt="Hero"
                        />
                    )}

                    <div className="absolute bottom-0 left-0 p-6 md:p-12 z-20 w-full md:w-3/4">
                        {dailyFeatured ? (
                            <div key={dailyFeatured.id} className="animate-in slide-in-from-bottom-4 fade-in duration-500">
                                <div className="flex flex-wrap items-center gap-2 mb-3">
                                    <span className="text-red-600 border border-red-600 font-bold px-1 text-[10px] rounded-sm tracking-widest uppercase">ACME ORIGINAL</span>
                                    <span className="text-white font-mono text-[10px] uppercase opacity-80">{dailyFeatured.category || 'MISTÉRIO PÚBLICO'}</span>
                                    {dailyFeatured.difficulty && <span className="bg-gray-800 text-gray-300 font-bold px-1.5 py-0.5 text-[10px] rounded-sm uppercase">{dailyFeatured.difficulty}</span>}
                                    {dailyFeatured.estimatedTime && <span className="bg-gray-800 text-gray-300 font-bold px-1.5 py-0.5 text-[10px] rounded-sm uppercase">{dailyFeatured.estimatedTime}</span>}
                                    {dailyFeatured.isSpecialSeason && <span className="bg-amber-600 text-white font-bold px-1.5 py-0.5 text-[10px] rounded-sm uppercase">TEMPORADA VIP</span>}
                                </div>
                                <h1 className="text-4xl md:text-6xl font-black text-white mb-4 uppercase leading-none tracking-tighter drop-shadow-xl">{dailyFeatured.title}</h1>
                                <p className="text-gray-300 text-sm md:text-base mb-6 line-clamp-3 max-w-2xl drop-shadow-md">{dailyFeatured.description}</p>
                                <div className="flex gap-4">
                                    {(dailyFeatured.scheduledDate && dailyFeatured.scheduledDate > Date.now()) ? (
                                        <button disabled className="bg-blue-600 hover:bg-blue-600 text-white px-6 md:px-8 py-2 md:py-3 rounded font-bold flex items-center gap-2 text-sm md:text-base cursor-not-allowed opacity-90 shadow-lg">
                                            <Clock className="w-5 h-5 md:w-6 md:h-6" /> EM BREVE
                                        </button>
                                    ) : (
                                        <button onClick={() => setSelectedCaseModal(dailyFeatured)} className="bg-white hover:bg-gray-200 text-black px-6 md:px-8 py-2 md:py-3 rounded font-bold flex items-center gap-2 transition-colors text-sm md:text-base shadow-lg hover:scale-105 active:scale-95">
                                            <PlayCircle className="w-5 h-5 md:w-6 md:h-6 fill-current" /> ACESSAR ARQUIVO
                                        </button>
                                    )}
                                </div>
                            </div>
                        ) : (
                            <>
                                <h1 className="text-4xl md:text-6xl font-black text-white mb-4 uppercase leading-none drop-shadow-xl">ARQUIVOS ACME</h1>
                                <p className="text-gray-300 text-sm md:text-base mb-6 max-w-xl">Nenhum caso novo no momento. Aguarde atualizações do departamento.</p>
                            </>
                        )}
                    </div>
                    {/* Carousel Indicators */}
                    {featuredCases.length > 1 && (
                        <div className="absolute bottom-6 right-6 md:bottom-12 md:right-12 z-20 flex gap-2">
                            {featuredCases.map((c, idx) => (
                                <button key={c.id} onClick={() => setHeroIndex(idx)} className={`h-1.5 rounded-full transition-all duration-300 ${heroIndex === idx ? 'w-6 bg-white' : 'w-2 bg-white/40 hover:bg-white/60'}`} />
                            ))}
                        </div>
                    )}
                </div>

                {/* Carousels Container */}
                <div className="px-4 md:px-12 mt-4 md:mt-8 z-20 relative space-y-8">
                    {/* Carousel 1: Tutorial (Priority) */}
                    {tutorialCase && !gameState.activeCases.find(c => c.id === tutorialCase.id) && !gameState.archivedCases.find(c => c.id === tutorialCase.id) && (
                        <CarouselRow title="Treinamento de Agente" cases={[tutorialCase] as CaseData[]} onSelect={(c) => setSelectedCaseModal(c as CaseData)} isVIP={true} />
                    )}


                    {/* Carousel 1: Continue Watching (Active Cases) */}
                    {gameState.activeCases.length > 0 && (
                        <CarouselRow
                            title="Continue Investigando"
                            cases={gameState.activeCases.map(activeC => {
                                const globalC = globalCases.find(g => g.id === activeC.id) || (staticTutorial?.id === activeC.id ? staticTutorial : null);
                                return {
                                    ...activeC,
                                    thumbImageUrl: globalC?.thumbImageUrl || activeC.thumbImageUrl,
                                    heroImageUrl: globalC?.heroImageUrl || activeC.heroImageUrl,
                                    title: globalC?.title || activeC.title,
                                    description: globalC?.description || activeC.description,
                                    difficulty: globalC?.difficulty || activeC.difficulty,
                                    estimatedTime: globalC?.estimatedTime || activeC.estimatedTime,
                                    isSpecialSeason: globalC?.isSpecialSeason !== undefined ? globalC.isSpecialSeason : activeC.isSpecialSeason,
                                };
                            })}
                            onSelect={(c) => setSelectedCaseModal(c as CaseData)}
                            isVIP={isVIP}
                            isActive={true}
                        />
                    )}

                    {/* Carousel 2: Dynamic Categories from CMS */}
                    {sortedCategoryRows.map((row) => (
                        <React.Fragment key={`cat-${row.catLabel}`}>
                            <CarouselRow title={row.catLabel} cases={row.cases} onSelect={(c) => setSelectedCaseModal(c as CaseData)} isVIP={isVIP} />
                        </React.Fragment>
                    ))}

                    {/* Carousel 3: New Releases (Unseen Global without specific category mapping) */}
                    {uncategorizedSettings.length > 0 && (
                        <CarouselRow title="Casos Recentes" cases={uncategorizedSettings} onSelect={(c) => setSelectedCaseModal(c as CaseData)} isVIP={isVIP} />
                    )}


                    {/* Carousel 5: Solved/Archived */}
                    {gameState.archivedCases.length > 0 && (
                        <CarouselRow
                            title="Arquivos Mortos"
                            cases={gameState.archivedCases.map(archC => {
                                const globalC = globalCases.find(g => g.id === archC.id) || (staticTutorial?.id === archC.id ? staticTutorial : null);
                                return {
                                    ...archC,
                                    thumbImageUrl: globalC?.thumbImageUrl || archC.thumbImageUrl,
                                    heroImageUrl: globalC?.heroImageUrl || archC.heroImageUrl,
                                    title: globalC?.title || archC.title,
                                    description: globalC?.description || archC.description,
                                    difficulty: globalC?.difficulty || archC.difficulty,
                                    estimatedTime: globalC?.estimatedTime || archC.estimatedTime,
                                    isSpecialSeason: globalC?.isSpecialSeason !== undefined ? globalC.isSpecialSeason : archC.isSpecialSeason,
                                };
                            })}
                            onSelect={(c) => setSelectedCaseModal(c as CaseData)}
                            isVIP={true}
                            isArchived={true}
                        />
                    )}

                </div>
            </div>
        );
    };

    const CarouselRow = ({ title, cases, onSelect, isVIP, isActive, isArchived }: { title: string, cases: CaseData[], onSelect: (c: CaseData) => void, isVIP: boolean, isActive?: boolean, isArchived?: boolean }) => {
        return (
            <div className="flex flex-col group/row">
                <h3 className="text-gray-200 text-lg md:text-xl font-bold mb-3 px-2 flex items-center gap-2 transition-colors hover:text-white cursor-pointer">
                    {title} <ChevronLeft className="w-4 h-4 text-transparent group-hover/row:text-white transition-all rotate-180 -ml-2" />
                </h3>
                <div className="flex gap-2 w-full overflow-x-auto pb-4 px-2 snap-x" style={{ scrollbarWidth: 'none' }}>
                    {cases.map(c => {
                        const hasPurchased = (unlockedCases || []).includes(c.id);
                        const isComingSoon = Boolean(c.scheduledDate && c.scheduledDate > Date.now());
                        const isLocked = (!isVIP && !isActive && !isArchived && c.id !== 'tutorial-case-001' && !hasPurchased) || isComingSoon;

                        let progress = 0;
                        if (isActive) {
                            progress = Math.min(100, Math.round((c.discoveredLocationIds.length / c.locations.length) * 100));
                        }

                        return (
                            <div
                                key={c.id}
                                onClick={() => onSelect(c)}
                                className="snap-start relative flex-shrink-0 w-[140px] md:w-[260px] flex flex-col group cursor-pointer"
                            >
                                <div className="relative aspect-[16/9] w-full bg-[#1a1a1a] rounded overflow-hidden transition-transform duration-300 group-hover:scale-105 origin-center shadow-lg border border-transparent group-hover:border-gray-500 mb-2">
                                    <img src={c.thumbImageUrl || `https://picsum.photos/seed/${c.id}_thumb/300/200`} className="absolute inset-0 w-full h-full object-cover opacity-70 group-hover:opacity-100 transition-opacity duration-300" alt={c.title} />
                                    <div className="absolute inset-0 bg-gradient-to-t from-[#141414] via-[#141414]/10 to-transparent"></div>

                                    {isActive && (
                                        <div className="absolute bottom-0 left-0 w-full">
                                            <div className="h-[4px] bg-gray-600 rounded-b overflow-hidden">
                                                <div className="h-full bg-red-600 rounded-b" style={{ width: `${progress}%` }}></div>
                                            </div>
                                        </div>
                                    )}

                                    {isArchived && (
                                        <div className="absolute top-2 right-2 bg-black/80 px-2 py-1 rounded text-[8px] md:text-[10px] text-gray-300 font-bold uppercase backdrop-blur-md border border-gray-700 shadow-md">
                                            {(c as any).status === 'solved' ? '✅ Resolvido' : '❌ Falha'}
                                        </div>
                                    )}

                                    {c.isExtra && !isActive && !isArchived && (
                                        <div className="absolute top-2 left-2 bg-purple-900/90 text-purple-200 px-2 py-0.5 rounded text-[8px] font-bold uppercase border border-purple-500/50 shadow-md">
                                            EXTRA
                                        </div>
                                    )}

                                    {isComingSoon ? (
                                        <div className="absolute inset-0 bg-black/80 flex flex-col items-center justify-center backdrop-blur-md transition-all duration-300">
                                            <Clock className="w-8 h-8 text-blue-400 mb-2 drop-shadow-lg" />
                                            <span className="text-blue-400 text-[10px] font-bold font-mono tracking-widest bg-blue-900/50 px-2 py-1 rounded border border-blue-500/30 text-center">
                                                EM BREVE
                                            </span>
                                        </div>
                                    ) : isLocked ? (
                                        <div className="absolute inset-0 bg-black/70 flex flex-col items-center justify-center backdrop-blur-sm transition-all duration-300 group-hover:bg-black/80">
                                            <Lock className="w-8 h-8 text-amber-500 mb-2 drop-shadow-lg" />
                                            <span className="text-amber-500 text-[10px] font-bold font-mono tracking-widest bg-amber-900/50 px-2 py-1 rounded border border-amber-500/30 text-center">
                                                DESBLOQUEAR
                                            </span>
                                        </div>
                                    ) : null}
                                </div>

                                <div className="px-1 mt-1 transition-transform duration-300 group-hover:translate-x-1">
                                    <h4 className="text-gray-300 group-hover:text-amber-400 font-bold text-xs md:text-sm leading-tight line-clamp-2 drop-shadow-sm">{c.title}</h4>
                                    {c.id !== 'tutorial-case-001' && (
                                        <div className="text-[9px] md:text-[10px] text-gray-500 mt-1 font-mono uppercase">Lançado: {new Date(c.scheduledDate || c.createdAt).toLocaleDateString('pt-BR')}</div>
                                    )}
                                </div>
                            </div>
                        );
                    })}
                </div>
            </div>
        );
    };

    // 3. Clube ACME (Sistema de Pastas)
    const renderAcmeClub = () => {
        const isVIP = role === 'subscriber' || role === 'admin';

        return (
            <div className="flex flex-col h-full animate-in slide-in-from-right duration-500 bg-[#1a1a1a]">
                {/* Header ACME */}
                <div className="bg-[#2a2a2a] border-b border-[#333] p-4 flex justify-between items-center shadow-lg">
                    <div className="flex items-center gap-4">
                        <button onClick={() => setGameState(prev => ({ ...prev, dashboardView: 'main' }))} className="p-2 hover:bg-[#333] rounded text-gray-400 hover:text-white transition-colors">
                            <ChevronLeft className="w-5 h-5" />
                        </button>
                        <div>
                            <h2 className="text-xl font-bold text-amber-500 font-mono tracking-tighter flex items-center gap-2"><Shield className="w-5 h-5" /> AGÊNCIA ACME</h2>
                        </div>
                    </div>
                    <div className="flex items-center gap-4">
                        {isVIP && (
                            <button onClick={() => setIsAcmeGeneratorOpen(true)} className="bg-amber-700 hover:bg-amber-600 text-white px-4 py-2 rounded text-xs font-bold shadow-lg flex items-center gap-2">
                                <Zap className="w-3 h-3 fill-current" /> NOVA MISSÃO (IA)
                            </button>
                        )}
                        {UserBadge()}
                    </div>
                </div>

                {!isVIP ? (
                    <div className="flex-1 bg-[#333] flex flex-col items-center justify-center p-8 text-center">
                        <Lock className="w-20 h-20 text-gray-600 mb-6" />
                        <h3 className="text-2xl font-bold text-gray-400 mb-2">ACESSO RESTRITO</h3>
                        <p className="text-gray-500 max-w-md mb-8">Esta área é confidencial e exclusiva para membros VIP certificados. Adquira ou renove sua credencial para acessar novos casos ilimitados.</p>
                        <button onClick={() => setIsProfileOpen(true)} className="bg-amber-600 hover:bg-amber-500 text-black font-bold uppercase tracking-wider px-8 py-4 rounded-lg flex items-center gap-2 transition-all shadow-xl">
                            <Star className="w-5 h-5 fill-current" /> OBTER CREDENCIAL VIP
                        </button>
                    </div>
                ) : (
                    <>
                        {/* Tabs */}
                        <div className="flex bg-[#222] border-b border-[#333] px-4 pt-2 gap-1">
                            <button onClick={() => setAcmeTab('active')} className={`px-6 py-2 rounded-t-lg text-xs font-bold flex items-center gap-2 transition-colors ${acmeTab === 'active' ? 'bg-[#333] text-white border-t border-x border-[#444]' : 'text-gray-500 hover:text-gray-300'}`}>
                                <Briefcase className="w-3 h-3" /> EM ANDAMENTO ({gameState.activeCases.length})
                            </button>
                            <button onClick={() => setAcmeTab('archived')} className={`px-6 py-2 rounded-t-lg text-xs font-bold flex items-center gap-2 transition-colors ${acmeTab === 'archived' ? 'bg-[#333] text-white border-t border-x border-[#444]' : 'text-gray-500 hover:text-gray-300'}`}>
                                <Archive className="w-3 h-3" /> ARQUIVADOS ({gameState.archivedCases.filter(c => c.status === 'solved').length})
                            </button>
                            <button onClick={() => setAcmeTab('lost')} className={`px-6 py-2 rounded-t-lg text-xs font-bold flex items-center gap-2 transition-colors ${acmeTab === 'lost' ? 'bg-[#333] text-white border-t border-x border-[#444]' : 'text-gray-500 hover:text-gray-300'}`}>
                                <Skull className="w-3 h-3" /> PERDIDOS ({gameState.archivedCases.filter(c => c.status === 'failed').length})
                            </button>
                        </div>

                        {/* Content Area */}
                        <div className="flex-1 bg-[#333] p-6 overflow-y-auto">
                            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                                {acmeTab === 'active' && gameState.activeCases.map(c => (
                                    <div key={c.id} className="bg-[#222] p-4 rounded border-l-4 border-amber-500 shadow-md hover:translate-y-[-2px] transition-transform">
                                        <div className="flex justify-between items-start mb-2">
                                            <h4 className="font-bold text-white text-sm">{c.title}</h4>
                                            <span className="text-[10px] bg-amber-900/30 text-amber-500 px-2 py-1 rounded">ATIVO</span>
                                        </div>
                                        <p className="text-xs text-gray-400 mb-4 line-clamp-3 font-mono">{c.description}</p>
                                        <div className="flex items-center justify-between mt-auto">
                                            <span className="text-[10px] text-gray-500 font-mono">{getDayLabel(c.lastSavedTime)}</span>
                                            <button onClick={() => selectCase(c.id)} className="px-4 py-2 bg-[#444] hover:bg-[#555] text-white text-xs rounded font-bold transition-colors">CONTINUAR</button>
                                        </div>
                                    </div>
                                ))}
                                {acmeTab === 'archived' && gameState.archivedCases.filter(c => c.status === 'solved').map(c => (
                                    <div key={c.id} className="bg-[#222] p-4 rounded border-l-4 border-green-500 opacity-70">
                                        <div className="flex justify-between items-start mb-2">
                                            <h4 className="font-bold text-white text-sm line-through">{c.title}</h4>
                                            <span className="text-[10px] bg-green-900/30 text-green-500 px-2 py-1 rounded">RESOLVIDO</span>
                                        </div>
                                    </div>
                                ))}
                                {acmeTab === 'lost' && gameState.archivedCases.filter(c => c.status === 'failed').map(c => (
                                    <div key={c.id} className="bg-[#222] p-4 rounded border-l-4 border-red-500 opacity-70">
                                        <div className="flex justify-between items-start mb-2">
                                            <h4 className="font-bold text-white text-sm line-through">{c.title}</h4>
                                            <span className="text-[10px] bg-red-900/30 text-red-500 px-2 py-1 rounded">FALHA</span>
                                        </div>
                                    </div>
                                ))}
                                {acmeTab === 'active' && gameState.activeCases.length === 0 && (
                                    <div className="col-span-full flex flex-col items-center justify-center py-20 text-gray-600">
                                        <FolderOpen className="w-16 h-16 mb-4 opacity-20" />
                                        <p className="font-mono text-sm">Nenhum caso em andamento.</p>
                                    </div>
                                )}
                            </div>
                        </div>
                    </>
                )}
            </div>
        );
    };

    const isScouted = activeCase?.scoutedLocationIds?.includes(activeCase.currentLocationId || '') || false;

    const UserBadge = () => (
        <div
            onClick={() => setIsProfileOpen(true)}
            className="flex items-center gap-1.5 md:gap-3 bg-noir-950/80 backdrop-blur border border-noir-700 hover:border-amber-600/50 rounded-lg p-1 md:p-1.5 pr-2 md:pr-4 shadow-xl cursor-pointer transition-colors"
        >
            <div className="w-8 h-8 bg-neutral-800 rounded-full border border-amber-600 flex items-center justify-center overflow-hidden shrink-0">
                {authUser?.photoURL ? <img src={authUser.photoURL} alt="Agent" className="w-full h-full object-cover" /> : <User className="w-5 h-5 text-neutral-500" />}
            </div>
            <div className="flex flex-col">
                <span className="text-xs font-bold text-amber-500 uppercase tracking-widest leading-none">{authUser?.displayName || 'Agente'}</span>
                <span className="text-[10px] text-gray-400 font-mono mt-0.5 flex items-center gap-1">
                    {role === 'admin' ? 'DIRETOR' : role === 'subscriber' ? 'VIP' : getRankInfo(reputacao || 0).name.toUpperCase()}
                    {role !== 'admin' && (
                        <>
                            <span className="mx-0.5">•</span>
                            <span className="text-amber-500 flex items-center gap-0.5" title="Energia Total"><Search className="w-2.5 h-2.5" /> {role === 'subscriber' ? '∞' : totalLupas}</span>
                        </>
                    )}
                </span>
            </div>
        </div>
    );

    const handleBuyItem = (item: StoreItem) => {
        setCheckoutItem(item);
        setCpfInput('');
        setCheckoutError('');
    };
    const handleCancelPayment = async (paymentId: string) => {
        setPixData(null);
        setCheckoutItem(null);
        setCheckoutError('Tempo esgotado ou pagamento cancelado pelo usuário.');
        if (functions) {
            try {
                const cancelPayment = httpsCallable(functions, 'cancelAsaasPayment');
                await cancelPayment({ paymentId });
            } catch (err) {
                console.error("Erro ao cancelar o pagamento na provedora", err);
            }
        }
    };

    useEffect(() => {
        let timer: any;
        if (pixData) {
            timer = setInterval(() => {
                setCheckoutTimeLeft(prev => {
                    if (prev <= 1) {
                        clearInterval(timer);
                        handleCancelPayment(pixData.paymentId);
                        return 0;
                    }
                    return prev - 1;
                });
            }, 1000);
        } else {
            setCheckoutTimeLeft(300);
        }
        return () => clearInterval(timer);
    }, [pixData]);

    const confirmCheckout = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!functions || !checkoutItem) {
            showNotification("ERRO", "Serviço de pagamentos offline.", "alert");
            return;
        }

        if (!cpfInput || cpfInput.length < 11) {
            setCheckoutError('Por favor, informe um CPF/CNPJ válido.');
            return;
        }

        setIsCheckingOut(true);
        setCheckoutError('');
        setPixData(null);

        try {
            const createPayment = httpsCallable(functions, 'createAsaasPayment');
            const result = await createPayment({ itemId: checkoutItem.id, cpfCnpj: cpfInput });
            const data = result.data as any;

            if (data?.success && data?.encodedImage && data?.payload) {
                setPixData({
                    encodedImage: data.encodedImage,
                    payload: data.payload,
                    paymentId: data.paymentId
                });
            } else {
                setCheckoutError('Não foi possível gerar a chave PIX no momento. Tente novamente.');
            }
        } catch (error: any) {
            console.error("Erro no checkout:", error);
            setCheckoutError(error?.message || 'Erro inesperado ao conectar com a operadora financeira.');
        } finally {
            setIsCheckingOut(false);
        }
    };

    return (
        <div className="min-h-screen bg-black text-noir-text font-sans crt relative overflow-hidden selection:bg-noir-accent selection:text-black flex flex-col">
            {isTestMode && (
                <div className="bg-red-900/90 text-white text-[10px] font-bold py-1 px-4 text-center border-b border-red-700 z-[9999] uppercase tracking-widest animate-pulse flex items-center justify-center gap-2">
                    <AlertTriangle size={12} /> Sessão de Teste - O progresso não será salvo <AlertTriangle size={12} />
                </div>
            )}


            {/* LOJA MODAL (MERCADO PAGO) */}
            {isStoreOpen && (
                <div className="fixed inset-0 z-[999] bg-black/80 backdrop-blur-md flex items-center justify-center p-4 animate-in fade-in duration-300">
                    <div className="bg-[#111] border border-green-900/50 w-full max-w-5xl h-[85vh] rounded-2xl shadow-2xl flex flex-col overflow-hidden relative">
                        {/* Header */}
                        <div className="p-6 border-b border-[#222] flex justify-between items-start bg-gradient-to-r from-[#111] to-[#1a2e1a] shrink-0">
                            <div>
                                <h2 className="text-3xl font-black text-white tracking-tighter flex items-center gap-3">
                                    <ShoppingCart className="text-green-500 w-8 h-8" />
                                    DEPARTAMENTO DE SUPRIMENTOS
                                </h2>
                                <p className="text-sm font-mono text-green-500/70 mt-1 mb-6">Autorizados apenas pagamentos criptografados oficiais (Asaas).</p>

                                <div className="flex gap-6 mt-2">
                                    <button onClick={() => setStoreTab('products')} className={`text-sm font-bold pb-2 border-b-2 transition-colors uppercase tracking-wider ${storeTab === 'products' ? 'text-green-500 border-green-500' : 'text-gray-500 border-transparent hover:text-gray-300'}`}>Produtos Disponíveis</button>
                                    <button onClick={() => setStoreTab('history')} className={`text-sm font-bold pb-2 border-b-2 transition-colors uppercase tracking-wider ${storeTab === 'history' ? 'text-green-500 border-green-500' : 'text-gray-500 border-transparent hover:text-gray-300'}`}>Meu Histórico</button>
                                </div>
                            </div>
                            <button onClick={() => { setIsStoreOpen(false); setCheckoutItem(null); if (pixData) handleCancelPayment(pixData.paymentId); setPixData(null); }} className="text-gray-500 hover:text-white bg-[#222] p-2 rounded-full transition-colors mt-1">
                                <X className="w-6 h-6" />
                            </button>
                        </div>

                        {/* Content / Items Grid */}
                        <div className="flex-1 overflow-y-auto p-8 bg-[#0a0a0a]">
                            {storeTab === 'products' ? (
                                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                                    {storeItems.length > 0 ? storeItems.map(item => (
                                        <div key={item.id} className="bg-[#1a1a1a] rounded-xl border border-[#333] hover:border-green-500 transition-all duration-300 flex flex-col overflow-hidden group hover:shadow-[0_0_30px_rgba(34,197,94,0.15)] relative">

                                            {item.highlight && (
                                                <div className="absolute top-4 -right-10 bg-red-600 text-white text-[10px] font-bold px-10 py-1 rotate-45 shadow-lg z-10 uppercase tracking-widest">
                                                    {item.highlight}
                                                </div>
                                            )}

                                            <div className="p-6 flex-1 flex flex-col items-center text-center relative z-0">
                                                {item.type === 'lupa' ? (
                                                    <div className="w-20 h-20 rounded-full bg-green-500/10 border border-green-500/30 flex items-center justify-center mb-4 group-hover:scale-110 transition-transform">
                                                        <Search className="w-10 h-10 text-green-500" />
                                                    </div>
                                                ) : item.type === 'vip' ? (
                                                    <div className="w-20 h-20 rounded-full bg-amber-500/10 border border-amber-500/30 flex items-center justify-center mb-4 group-hover:scale-110 transition-transform">
                                                        <Star className="w-10 h-10 text-amber-500" />
                                                    </div>
                                                ) : (
                                                    <div className="w-20 h-20 rounded-full bg-purple-500/10 border border-purple-500/30 flex items-center justify-center mb-4 group-hover:scale-110 transition-transform">
                                                        <FolderOpen className="w-10 h-10 text-purple-500" />
                                                    </div>
                                                )}

                                                <h3 className="text-xl font-black text-white mb-2">{item.title}</h3>
                                                <p className="text-gray-400 text-sm mb-6 leading-relaxed line-clamp-3">{item.description}</p>

                                                <div className="mt-auto flex flex-col items-center">
                                                    {item.discount && (
                                                        <span className="text-xs text-gray-500 line-through mb-1">
                                                            R$ {(item.price / (1 - item.discount / 100)).toFixed(2)}
                                                        </span>
                                                    )}
                                                    <span className="text-3xl font-black text-green-400 font-mono tracking-tighter">
                                                        R$ {item.price.toFixed(2)}
                                                    </span>
                                                </div>
                                            </div>

                                            <div className="p-4 bg-[#111] border-t border-[#333]">
                                                <button
                                                    onClick={() => handleBuyItem(item)}
                                                    disabled={isCheckingOut}
                                                    className="w-full bg-green-600 hover:bg-green-500 disabled:bg-gray-700 disabled:text-gray-500 text-black font-black uppercase tracking-widest py-4 rounded-lg flex justify-center items-center gap-2 transition-transform transform active:scale-95 shadow-lg"
                                                >
                                                    <ShoppingBag className="w-5 h-5" />
                                                    {isCheckingOut ? 'PROCESSANDO...' : 'ADQUIRIR AGORA'}
                                                </button>
                                            </div>
                                        </div>
                                    )) : (
                                        <div className="col-span-full flex flex-col items-center justify-center py-20 opacity-50">
                                            <ShoppingCart className="w-24 h-24 text-gray-700 mb-6" />
                                            <h3 className="text-2xl font-bold text-gray-500">ESTOQUE VAZIO</h3>
                                            <p className="text-gray-600">O departamento logístico não disponibilizou suprimentos hoje.</p>
                                        </div>
                                    )}
                                </div>
                            ) : (
                                <div className="max-w-3xl mx-auto space-y-4">
                                    {userSales.length === 0 ? (
                                        <div className="text-center py-20 opacity-50 flex flex-col items-center">
                                            <Archive className="w-16 h-16 text-gray-700 mb-4" />
                                            <h3 className="text-xl font-bold text-gray-500">NENHUMA COMPRA REALIZADA</h3>
                                            <p className="text-gray-600">Seu histórico de suprimentos está vazio.</p>
                                        </div>
                                    ) : (
                                        userSales.map(sale => (
                                            <div key={sale.id} className="bg-[#1a1a1a] p-5 rounded-lg border border-[#333] flex justify-between items-center shadow-lg transition-transform hover:scale-[1.01]">
                                                <div className="flex items-center gap-4">
                                                    <div className={`w-12 h-12 rounded-full flex items-center justify-center border ${sale.status === 'approved' ? 'bg-green-900/20 border-green-500/30' : 'bg-amber-900/20 border-amber-500/30'}`}>
                                                        <ShoppingBag className={`w-5 h-5 ${sale.status === 'approved' ? 'text-green-500' : 'text-amber-500'}`} />
                                                    </div>
                                                    <div>
                                                        <h4 className="font-bold text-white text-lg">{sale.itemTitle || sale.itemId || 'Pacote Identificado pelo ID'}</h4>
                                                        <p className="text-xs text-gray-500 font-mono mt-1">{sale.purchasedAt ? new Date(sale.purchasedAt.toDate()).toLocaleString('pt-BR') : (sale.createdAt ? new Date(sale.createdAt.toDate()).toLocaleString('pt-BR') : 'Data Indisponível')}</p>
                                                    </div>
                                                </div>
                                                <div className="text-right">
                                                    <div className="text-green-400 font-black tracking-tighter text-xl">R$ {sale.price.toFixed(2)}</div>
                                                    <div className={`text-[10px] font-bold uppercase tracking-widest mt-1 px-2 py-0.5 rounded inline-block ${sale.status === 'approved' ? 'bg-green-900/50 text-green-400 border border-green-800' : sale.status === 'cancelled' ? 'bg-red-900/50 text-red-500 border border-red-800' : 'bg-amber-900/50 text-amber-500 border border-amber-800'}`}>
                                                        {sale.status === 'approved' ? 'Aprovado' : sale.status === 'pending' ? 'Aguardando Pagamento' : sale.status === 'cancelled' ? 'Cancelado' : sale.status}
                                                    </div>
                                                </div>
                                            </div>
                                        ))
                                    )}
                                </div>
                            )}
                        </div>
                    </div>

                    {/* PIX Modal / Overlay */}
                    {pixData ? (
                        <div className="absolute inset-0 bg-black/95 z-50 flex flex-col items-center justify-center p-6 animate-in zoom-in-95 duration-300">
                            <div className="bg-[#1a1a1a] border-2 border-green-500 rounded-xl p-8 max-w-md w-full text-center relative shadow-[0_0_50px_rgba(34,197,94,0.3)]">
                                <button onClick={() => { if (pixData) handleCancelPayment(pixData.paymentId); setPixData(null); setCheckoutItem(null); }} className="absolute top-4 right-4 text-gray-400 hover:text-white transition-colors">
                                    <X className="w-6 h-6" />
                                </button>

                                <h2 className="text-2xl font-black text-green-500 mb-2 uppercase tracking-tight">PAGAMENTO PIX CHRONOS</h2>
                                <p className="text-gray-400 text-sm mb-6">Abra o aplicativo do seu banco e escaneie o código abaixo. A liberação ocorrerá imediatamente após a aprovação.</p>

                                <div className="bg-white p-4 rounded-lg mb-6 inline-block">
                                    <img src={`data:image/png;base64,${pixData.encodedImage}`} alt="QR Code Pix" className="w-48 h-48 md:w-64 md:h-64 object-contain" />
                                </div>

                                <div className="mb-6">
                                    <p className="text-xs text-gray-500 font-bold mb-2 uppercase">Ou copie o código (PIX Copia e Cola)</p>
                                    <div className="flex gap-2">
                                        <input type="text" readOnly value={pixData.payload} className="flex-1 bg-black border border-[#333] text-gray-300 rounded px-3 py-2 text-xs font-mono outline-none" />
                                        <button
                                            onClick={() => {
                                                navigator.clipboard.writeText(pixData.payload);
                                                showNotification("SUCESSO", "Código Pix copiado para a área de transferência.", "success");
                                            }}
                                            className="bg-[#222] hover:bg-[#333] text-white px-4 py-2 rounded text-xs font-bold transition-colors shadow"
                                        >
                                            COPIAR
                                        </button>
                                    </div>
                                </div>
                                <div className="flex flex-col items-center justify-center gap-2 text-green-500/80">
                                    <div className="flex items-center gap-2 animate-pulse">
                                        <Search className="w-4 h-4" /> <span className="text-xs font-bold uppercase tracking-widest">Aguardando confirmação...</span>
                                    </div>
                                    <span className="text-xs text-green-500/50 font-mono mt-1">
                                        Expira em {Math.floor(checkoutTimeLeft / 60)}:{String(checkoutTimeLeft % 60).padStart(2, '0')}
                                    </span>
                                </div>
                            </div>
                        </div>
                    ) : checkoutItem ? (
                        <div className="absolute inset-0 bg-black/95 z-50 flex flex-col items-center justify-center p-6 animate-in zoom-in-95 duration-300">
                            <div className="bg-[#1a1a1a] border-2 border-green-500 rounded-xl p-8 max-w-md w-full relative shadow-[0_0_50px_rgba(34,197,94,0.3)]">
                                <button onClick={() => setCheckoutItem(null)} className="absolute top-4 right-4 text-gray-400 hover:text-white transition-colors">
                                    <X className="w-6 h-6" />
                                </button>
                                <h2 className="text-2xl font-black text-green-500 mb-6 uppercase tracking-tight text-center">Revisão de Compra</h2>

                                <div className="mb-6 p-4 bg-[#111] border border-[#333] rounded">
                                    <div className="text-sm text-gray-400 mb-1">Produto Selecionado:</div>
                                    <div className="text-lg font-bold text-white mb-2">{checkoutItem.title}</div>

                                    <div className="flex items-center justify-between border-b border-[#333] pb-4 mb-4">
                                        <span className="text-gray-400 text-sm">Total a pagar:</span>
                                        <span className="text-green-500 font-mono text-xl font-bold">R$ {checkoutItem.price.toFixed(2)}</span>
                                    </div>

                                    <div className="text-xs text-gray-500 mb-1">Dados do Agente:</div>
                                    <div className="text-white text-sm font-bold truncate">{authUser?.displayName || 'Agente Desconhecido'}</div>
                                    <div className="text-gray-400 text-sm truncate">{authUser?.email}</div>
                                </div>

                                <form onSubmit={confirmCheckout}>
                                    <div className="mb-6">
                                        <label className="block text-xs text-gray-400 uppercase font-bold mb-2">CPF / CNPJ do Pagador *</label>
                                        <input required type="text" value={cpfInput} onChange={e => setCpfInput(e.target.value)} placeholder="000.000.000-00" className="w-full bg-black border border-[#444] text-white p-3 rounded focus:border-green-500 outline-none transition-colors" />
                                        <p className="text-[10px] text-gray-500 mt-2">Obrigatoriedade da Instituição de Pagamento (Asaas) e do Banco Central.</p>
                                    </div>

                                    {checkoutError && <div className="mb-4 text-amber-500 text-sm bg-amber-900/20 p-3 rounded border border-amber-900 shadow-inner font-bold flex items-start gap-2"> {checkoutError}</div>}

                                    <button disabled={isCheckingOut} type="submit" className="w-full bg-green-600 hover:bg-green-500 disabled:bg-gray-700 disabled:text-gray-500 text-black font-black uppercase tracking-widest py-4 rounded-lg flex justify-center items-center gap-2 transition-transform transform active:scale-95 shadow-lg">
                                        {isCheckingOut ? 'PROCESSANDO...' : 'CONFIRMAR E GERAR PIX'}
                                    </button>
                                </form>
                            </div>
                        </div>
                    ) : null}

                    {/* Error Display */}
                    {checkoutError && (
                        <div className="absolute bottom-10 left-1/2 transform -translate-x-1/2 bg-red-900 border border-red-500 text-white px-6 py-4 rounded-lg shadow-2xl flex items-center gap-3 z-50">
                            <BadgeAlert className="w-6 h-6" />
                            <div>
                                <p className="font-bold text-sm">Operação Abortada</p>
                                <p className="text-xs">{checkoutError}</p>
                            </div>
                            <button onClick={() => setCheckoutError('')} className="ml-4 text-white/50 hover:text-white"><X className="w-5 h-5" /></button>
                        </div>
                    )}
                </div>
            )
            }

            {/* Selected Case Modal Checks & Methods */}
            {(() => {
                if (!selectedCaseModal) return null;
                const isComingSoon = Boolean(selectedCaseModal.scheduledDate && selectedCaseModal.scheduledDate > Date.now());
                const hasPurchased = (unlockedCases || []).includes(selectedCaseModal.id) || (gameState.archivedCases.find(c => c.id === selectedCaseModal.id)?.status === 'unlocked') || selectedCaseModal.id === 'tutorial-case-001';
                const isVIP = role === 'subscriber' || role === 'admin';
                const hasUnrestrictedVipAccess = isVIP;
                const isLocked = (!hasPurchased && !hasUnrestrictedVipAccess) || isComingSoon;

                const performCaseEntry = (targetCase: CaseData) => {
                    if (gameState.activeCases.find(c => c.id === targetCase.id)) {
                        selectCase(targetCase.id);
                    } else if (targetCase.id === 'tutorial-case-001') {
                        createCase(true);
                    } else {
                        startGlobalCase(targetCase);
                    }
                };

                const isOngoingAndRevoked = !hasPurchased && !hasUnrestrictedVipAccess && gameState.activeCases.find(c => c.id === selectedCaseModal.id);

                return (
                    <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-[999] flex items-center justify-center p-4 animate-in fade-in duration-300">
                        <div className="bg-[#111] border border-gray-800 rounded-lg max-w-lg w-full overflow-hidden shadow-2xl relative">
                            <div className="relative h-48 w-full group">
                                <img src={selectedCaseModal.thumbImageUrl || `https://picsum.photos/seed/${selectedCaseModal.id}_thumb/300/200`} className="w-full h-full object-cover opacity-50" alt="Capa" />
                                <div className="absolute inset-0 bg-gradient-to-t from-[#111] to-transparent"></div>
                                <button onClick={() => setSelectedCaseModal(null)} className="absolute top-4 right-4 text-gray-400 hover:text-white bg-black/50 p-1.5 rounded-full z-10 transition-colors">
                                    <X className="w-5 h-5" />
                                </button>
                                {isLocked && <div className="absolute top-4 left-4 flex gap-2">
                                    <span className="bg-amber-600 text-white text-[10px] font-bold px-2 py-0.5 rounded shadow flex items-center gap-1 uppercase tracking-widest"><Lock className="w-3 h-3" /> Arquivo Restrito</span>
                                </div>}
                            </div>

                            <div className="p-6 md:p-8 -mt-6 relative z-10">
                                <h3 className="text-xl md:text-2xl font-black text-white mb-2 drop-shadow-md">{selectedCaseModal.title}</h3>
                                <div className="flex flex-wrap items-center gap-2 mb-4">
                                    <div className="text-xs text-amber-500 font-mono uppercase">Lançado: {new Date(selectedCaseModal.scheduledDate || selectedCaseModal.createdAt).toLocaleDateString('pt-BR')}</div>
                                    {selectedCaseModal.difficulty && <span className="bg-[#222] text-gray-300 font-bold px-1.5 py-0.5 text-[10px] rounded uppercase border border-gray-700">{selectedCaseModal.difficulty}</span>}
                                    {selectedCaseModal.estimatedTime && <span className="bg-[#222] text-gray-300 font-bold px-1.5 py-0.5 text-[10px] rounded uppercase border border-gray-700">{selectedCaseModal.estimatedTime}</span>}
                                    {selectedCaseModal.isSpecialSeason && <span className="bg-amber-900/50 text-amber-500 font-bold px-1.5 py-0.5 text-[10px] rounded uppercase border border-amber-900/80">TEMPORADA VIP</span>}
                                </div>

                                <p className="text-gray-300 text-sm leading-relaxed mb-6 line-clamp-4">{selectedCaseModal.description}</p>

                                {selectedCaseModal.scheduledDate && selectedCaseModal.scheduledDate > Date.now() ? (
                                    <div className="bg-blue-950/30 border border-blue-900/50 p-4 rounded-lg mb-6 flex gap-3 text-blue-500">
                                        <Clock className="w-6 h-6 shrink-0 mt-0.5 opacity-80" />
                                        <div>
                                            <h4 className="font-bold text-sm mb-1 uppercase tracking-wide">Lançamento em Breve</h4>
                                            <p className="text-xs opacity-80 leading-snug text-blue-300">Este arquivo ainda está sendo processado pela Diretoria. Volte no dia {new Date(selectedCaseModal.scheduledDate).toLocaleDateString('pt-BR')}.</p>
                                        </div>
                                    </div>
                                ) : selectedCaseModal.isSpecialSeason && !hasUnrestrictedVipAccess ? (
                                    <div className="bg-red-950/30 border border-red-900/50 p-4 rounded-lg mb-6 flex gap-3 text-red-500">
                                        <Lock className="w-6 h-6 shrink-0 mt-0.5 opacity-80" />
                                        <div>
                                            <h4 className="font-bold text-sm mb-1 uppercase tracking-wide">Exclusivo VIP / Temporada</h4>
                                            <p className="text-xs opacity-80 leading-snug text-red-300">Este é um Arquivo Especial de Temporada altamente confidencial. Você precisa de uma Assinatura VIP ou de um Código de Resgate para desbloqueá-lo.</p>
                                        </div>
                                    </div>
                                ) : isOngoingAndRevoked ? (
                                    <div className="bg-amber-950/30 border border-amber-900/50 p-4 rounded-lg mb-6 flex gap-3 text-amber-500">
                                        <Lock className="w-6 h-6 shrink-0 mt-0.5 opacity-80" />
                                        <div>
                                            <h4 className="font-bold text-sm mb-1 uppercase tracking-wide">Acesso Revogado</h4>
                                            <p className="text-xs opacity-80 leading-snug text-amber-300">Sua Credencial VIP expirou. Para continuar esta investigação, adquira um novo suborno, utilize chave de arquivo ou assine novamente.</p>
                                        </div>
                                    </div>
                                ) : hasPurchased ? (
                                    <div className="flex flex-col gap-3 mb-6">
                                        <button onClick={() => { setSelectedCaseModal(null); performCaseEntry(selectedCaseModal); }} className="bg-amber-600 hover:bg-amber-500 text-black font-black uppercase tracking-widest py-4 rounded-lg flex justify-center items-center gap-2 transition-transform transform active:scale-95 shadow-lg w-full">
                                            CONTINUAR INVESTIGAÇÃO
                                        </button>
                                    </div>
                                ) : hasUnrestrictedVipAccess ? (
                                    <div className="flex flex-col gap-3 mb-6">
                                        <button onClick={() => { setSelectedCaseModal(null); performCaseEntry(selectedCaseModal); }} className="bg-noir-900 border border-noir-600 hover:border-amber-500 text-amber-500 font-bold uppercase tracking-widest py-4 rounded-lg flex flex-col justify-center items-center gap-1 transition-all shadow-lg w-full group">
                                            <span className="flex items-center gap-2"><Star className="w-5 h-5 fill-current" /> ACESSO VIP LIBERADO</span>
                                            <span className="text-[10px] text-gray-500 font-mono group-hover:text-amber-500/70">Entrar sem gastar chaves</span>
                                        </button>
                                    </div>
                                ) : (
                                    <div className="flex flex-col gap-3 mb-6">
                                        {/* Option A */}
                                        <button
                                            onClick={() => handleUnlockWithAd(selectedCaseModal)}
                                            disabled={(dailyAdUnlocks || 0) >= 3}
                                            className="bg-neutral-900 border border-neutral-700 hover:border-amber-500/50 p-4 rounded-lg flex items-center justify-between text-left group transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                                        >
                                            <div className="flex gap-3">
                                                <PlayCircle className="w-8 h-8 shrink-0 mt-1 text-blue-500 group-hover:text-blue-400" />
                                                <div>
                                                    <h4 className="font-bold text-sm text-neutral-200 uppercase tracking-wide group-hover:text-white">Opção A: Subornar Informante</h4>
                                                    <p className="text-xs text-neutral-400 mt-1">Nossos contatos exigem que ouçamos sua mensagem. (Assistir 30s de Anúncio). Limite Diário: {dailyAdUnlocks || 0}/3</p>
                                                </div>
                                            </div>
                                        </button>

                                        {/* Option B */}
                                        <button
                                            onClick={() => handleUnlockWithKey(selectedCaseModal)}
                                            className="bg-neutral-900 border border-neutral-700 hover:border-amber-500/50 p-4 rounded-lg flex items-center justify-between text-left group transition-all"
                                        >
                                            <div className="flex gap-3">
                                                <Lock className="w-8 h-8 shrink-0 mt-1 text-amber-500 group-hover:text-amber-400" />
                                                <div>
                                                    <h4 className="font-bold text-sm text-neutral-200 uppercase tracking-wide group-hover:text-white">Opção B: Chave de Arquivo</h4>
                                                    <p className="text-xs text-neutral-400 mt-1">Desbloqueio Imediato e Restrito. Requer intervenção direta. Seu saldo atual: {archiveKeys || 0} Chaves.</p>
                                                </div>
                                            </div>
                                        </button>
                                    </div>
                                )}

                                <div className="flex gap-3 mt-4">
                                    <button
                                        onClick={() => setSelectedCaseModal(null)}
                                        className="px-4 py-3 bg-[#222] hover:bg-[#333] border border-gray-700 rounded text-sm text-gray-300 font-bold transition-colors w-full"
                                    >
                                        VOLTAR À LISTA
                                    </button>
                                    {selectedCaseModal.isSpecialSeason && (!selectedCaseModal.scheduledDate || selectedCaseModal.scheduledDate <= Date.now()) && (
                                        <button
                                            onClick={() => { setSelectedCaseModal(null); setIsProfileOpen(true); }}
                                            className="px-6 py-3 bg-[#444] hover:bg-gray-600 text-white font-bold text-sm rounded shadow drop-shadow-md transition-colors flex justify-center items-center gap-2"
                                        >
                                            RESGATAR CÓDIGO VIP
                                        </button>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>
                );
            })()}

            {/* Acme Generator Modal */}
            {isAcmeGeneratorOpen && (
                <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-[999] flex items-center justify-center p-4 animate-in fade-in duration-300">
                    <div className="bg-[#111] border border-amber-900/50 rounded-lg max-w-md w-full overflow-hidden shadow-2xl relative flex flex-col">
                        <div className="absolute inset-0 bg-[url('https://www.transparenttextures.com/patterns/cubes.png')] opacity-10 pointer-events-none"></div>

                        <div className="p-4 border-b border-[#222] flex justify-between items-center relative z-10">
                            <h3 className="text-lg font-bold text-amber-500 font-mono tracking-tighter flex items-center gap-2">
                                <Zap className="w-5 h-5 fill-current" /> GERADOR ACME
                            </h3>
                            <button onClick={() => setIsAcmeGeneratorOpen(false)} className="text-gray-400 hover:text-white transition-colors">
                                <X size={20} />
                            </button>
                        </div>

                        <div className="p-6 relative z-10 flex flex-col gap-4">
                            <p className="text-xs text-gray-400 leading-relaxed mb-2">
                                O Clube ACME possui um supercomputador investigativo. Forneça as diretrizes para gerar um caso exclusivo para sua mesa.
                            </p>

                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-[10px] font-bold text-neutral-400 uppercase tracking-widest mb-1.5">Tamanho do Caso</label>
                                    <select
                                        value={acmeSettings.duration}
                                        onChange={(e) => setAcmeSettings({ ...acmeSettings, duration: e.target.value as any })}
                                        className="w-full bg-[#1a1a1a] border border-[#333] rounded px-3 py-2 text-sm text-white focus:border-amber-500 outline-none"
                                    >
                                        <option value="Curto">Curto (Rápido)</option>
                                        <option value="Médio">Médio (Padrão)</option>
                                        <option value="Longo">Longo (Complexo)</option>
                                    </select>
                                </div>
                                <div>
                                    <label className="block text-[10px] font-bold text-neutral-400 uppercase tracking-widest mb-1.5">Dificuldade</label>
                                    <select
                                        value={acmeSettings.difficulty}
                                        onChange={(e) => setAcmeSettings({ ...acmeSettings, difficulty: e.target.value as any })}
                                        className="w-full bg-[#1a1a1a] border border-[#333] rounded px-3 py-2 text-sm text-white focus:border-amber-500 outline-none"
                                    >
                                        <option value="Fácil">Fácil</option>
                                        <option value="Médio">Médio</option>
                                        <option value="Difícil">Difícil</option>
                                    </select>
                                </div>
                            </div>

                            <div>
                                <label className="block text-[10px] font-bold text-neutral-400 uppercase tracking-widest mb-1.5 flex items-center gap-1.5">
                                    <Lock size={12} className="text-amber-500" /> Semente Criativa (Obrigatório)
                                </label>
                                <textarea
                                    value={acmeSettings.seed}
                                    onChange={(e) => setAcmeSettings({ ...acmeSettings, seed: e.target.value })}
                                    maxLength={200}
                                    placeholder="Ex: Um assassinato em um teatro francês nos anos 40, envolvendo atores rivais..."
                                    className="w-full bg-[#1a1a1a] border border-[#333] rounded px-3 py-2 text-sm text-white focus:border-amber-500 outline-none resize-none h-20"
                                />
                                <div className="text-right text-[10px] text-gray-500 mt-1">{acmeSettings.seed.length}/200</div>
                            </div>

                            <div className="flex justify-between items-center bg-noir-900/50 p-3 rounded border border-noir-700 mt-2">
                                <span className="text-[10px] text-indigo-400 font-mono flex items-center gap-1.5">
                                    <RefreshCw className="w-3 h-3" /> USOS DIÁRIOS (GERAÇÃO)
                                </span>
                                <span className="text-sm text-indigo-400 font-bold font-mono">{dailyAcmeCreations || 0}/5</span>
                            </div>

                            <button
                                onClick={createAcmeCase}
                                disabled={!acmeSettings.seed.trim() || (dailyAcmeCreations || 0) >= 5}
                                className="w-full bg-amber-600 hover:bg-amber-500 text-black font-black uppercase tracking-widest py-3 rounded mt-2 flex justify-center items-center gap-2 transition-all disabled:opacity-50 disabled:cursor-not-allowed shadow-lg"
                            >
                                INICIAR INVESTIGAÇÃO
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Corregedoria Modal */}
            {corregedoriaModal && (
                <div className="fixed inset-0 z-[110] bg-black/90 backdrop-blur-md flex items-center justify-center p-4">
                    <div className="bg-red-950/80 border border-red-900 w-full max-w-sm rounded-lg shadow-2xl overflow-hidden relative flex flex-col p-6 text-center shadow-[0_0_50px_rgba(220,38,38,0.3)]">
                        <Siren className="w-16 h-16 text-red-500 mx-auto mb-4 animate-pulse" />
                        <h2 className="text-2xl font-black text-white tracking-widest uppercase mb-2">CORREGEDORIA</h2>
                        <p className="text-neutral-300 font-mono text-sm mb-6">
                            Você prendeu um cidadão inocente. O departamento sofrerá um forte processo administrativo e sua reputação foi manchada.
                        </p>
                        
                        <div className="bg-black/50 p-4 rounded-lg border border-red-900/50 mb-6">
                            <p className="text-red-400 font-bold mb-1">- {corregedoriaModal.repLost} Pontos de Reputação</p>
                            <p className="text-sm text-neutral-400 font-mono">Nova Pontuação: {corregedoriaModal.newRep} pts</p>
                        </div>

                        {corregedoriaModal.downranked && (
                            <div className="bg-red-900/40 text-red-200 p-3 rounded mb-6 text-xs font-bold border border-red-500/30 uppercase tracking-widest">
                                ⚠️ ALERTA DE REBAIXAMENTO ⚠️<br/>Sua patente atual caiu para {corregedoriaModal.rankName}.
                            </div>
                        )}

                        <button 
                            onClick={() => setCorregedoriaModal(null)}
                            className="bg-red-900 hover:bg-red-800 text-white font-bold py-3 uppercase tracking-wider rounded transition-colors w-full"
                        >
                            ACEITAR PUNIÇÃO
                        </button>
                    </div>
                </div>
            )}

            {/* Promotion Modal */}
            {promotionModal && (
                <div className="fixed inset-0 z-[110] bg-black/90 backdrop-blur-md flex items-center justify-center p-4">
                    <div className="bg-amber-950/90 border border-amber-600 w-full max-w-sm rounded-lg shadow-2xl overflow-hidden relative flex flex-col p-6 text-center shadow-[0_0_50px_rgba(217,119,6,0.3)]">
                        <Medal className="w-16 h-16 text-amber-500 mx-auto mb-4 drop-shadow-[0_0_15px_rgba(245,158,11,0.5)]" />
                        <h2 className="text-2xl font-black text-amber-500 tracking-widest uppercase mb-2">PROMOÇÃO</h2>
                        <p className="text-neutral-200 font-mono text-sm mb-6">
                            Excelentíssimo trabalho, Detetive. Sua dedicação ao resolver casos não passou despercebida pela Direção. 
                        </p>
                        
                        <div className="bg-black/50 p-4 rounded-lg border border-amber-900/50 mb-6 font-mono text-sm">
                            <p className="text-neutral-400 mb-1">Nova Patente Oficial:</p>
                            <p className="text-white font-bold text-lg mb-3 tracking-widest uppercase">{promotionModal.newRankName}</p>
                            <div className="w-full h-px bg-amber-900/30 my-3"></div>
                            <p className="text-amber-500 font-bold">{promotionModal.rewardMsg}</p>
                        </div>

                        <button 
                            onClick={() => setPromotionModal(null)}
                            className="bg-amber-600 hover:bg-amber-500 text-black font-black py-3 uppercase tracking-wider rounded transition-colors w-full"
                        >
                            ASSINAR DOCUMENTOS
                        </button>
                    </div>
                </div>
            )}

            {/* Profile Modal */}
            {
                isProfileOpen && (
                    <div className="fixed inset-0 z-[100] bg-black/80 backdrop-blur-sm flex items-center justify-center p-4">
                        <div className="bg-neutral-900 border border-amber-900/50 w-full max-w-md max-h-[90vh] rounded-lg shadow-2xl overflow-hidden relative flex flex-col">
                            {/* Background Pattern */}
                            <div className="absolute inset-0 bg-[url('https://www.transparenttextures.com/patterns/cubes.png')] opacity-10 pointer-events-none"></div>

                            <button
                                onClick={() => setIsProfileOpen(false)}
                                className="absolute top-4 right-4 z-20 text-neutral-500 hover:text-white bg-black/50 p-2 rounded-full transition-colors"
                            >
                                <X size={20} />
                            </button>

                            <div className="p-6 relative overflow-y-auto custom-scrollbar flex-1">
                                <div className="flex items-center gap-4 mb-6">
                                    <div className="w-16 h-16 shrink-0 bg-neutral-800 rounded-full border-2 border-amber-600 flex items-center justify-center overflow-hidden">
                                        {authUser?.photoURL ? (
                                            <img src={authUser.photoURL} alt="Agent" className="w-full h-full object-cover" />
                                        ) : (
                                            <User className="w-8 h-8 text-neutral-500" />
                                        )}
                                    </div>
                                    <div className="flex flex-col items-start overflow-hidden w-full">
                                        <h2 className="text-lg font-bold text-amber-500 tracking-wider uppercase truncate w-full" title={authUser?.displayName || 'Agente Desconhecido'}>{authUser?.displayName || 'Agente Desconhecido'}</h2>
                                        <p className="text-[10px] text-neutral-400 font-mono mt-0.5">ID: {authUser?.uid.slice(0, 8)}</p>

                                        {(() => {
                                            const rank = getRankInfo(reputacao || 0);
                                            return (
                                                <div className="mt-1.5 flex flex-col items-start w-full">
                                                    <div className="inline-block px-2 py-0.5 bg-amber-900/30 border border-amber-900/50 rounded text-amber-400 text-[10px] font-bold uppercase tracking-widest flex items-center gap-1.5">
                                                        <Medal size={12} className="text-amber-500" />
                                                        {role === 'admin' ? 'DIRETOR GERAL' : role === 'subscriber' ? `VIP: ${rank.name.toUpperCase()}` : rank.name.toUpperCase()}
                                                    </div>
                                                    {role !== 'admin' && (
                                                        <div className="w-full max-w-[150px] mt-2 bg-neutral-900 h-1.5 rounded-full overflow-hidden border border-neutral-700">
                                                            {rank.level < 6 ? (
                                                                <div className="h-full bg-amber-500 transition-all" style={{ width: `${Math.max(0, Math.min(100, ((reputacao || 0) - rank.min) / (rank.max - rank.min + 1) * 100))}%` }} />
                                                            ) : (
                                                                <div className="h-full bg-amber-500 w-full" />
                                                            )}
                                                        </div>
                                                    )}
                                                    {role !== 'admin' && rank.level < 6 && (
                                                        <p className="text-[8px] text-neutral-500 font-mono mt-0.5">REP: {reputacao || 0}/{rank.max + 1}</p>
                                                    )}
                                                </div>
                                            );
                                        })()}

                                        {role === 'subscriber' && vipExpiresAt && (
                                            <p className="text-[10px] text-amber-500/80 mt-1.5 font-mono uppercase tracking-widest truncate">
                                                VIP Válido até {vipExpiresAt.toLocaleDateString()}
                                            </p>
                                        )}
                                    </div>
                                </div>

                                {/* Lupa / Energy Status */}
                                {role !== 'admin' && (
                                    <div className="mt-2 p-3 bg-neutral-950 border border-neutral-800 rounded-lg flex flex-col items-center">
                                        <div className="grid grid-cols-4 gap-1.5 mb-3 w-full border-b border-neutral-800 pb-3 text-center">
                                            <div className="flex flex-col justify-center items-center bg-neutral-900 p-1.5 rounded border border-neutral-800">
                                                <span className="flex items-center gap-0.5 text-[8px] md:text-[9px] text-gray-400 font-mono mb-1"><Search className="w-2.5 h-2.5 text-amber-500" /> DIÁRIAS</span>
                                                <span className="text-sm text-amber-500 font-bold font-mono">{role === 'subscriber' ? '∞' : (dailyLupas || 0)}</span>
                                            </div>
                                            <div className="flex flex-col justify-center items-center bg-neutral-900 p-1.5 rounded border border-neutral-800">
                                                <span className="flex items-center gap-0.5 text-[8px] md:text-[9px] text-gray-400 font-mono mb-1"><Search className="w-2.5 h-2.5 text-blue-500" /> ANÚNCIOS</span>
                                                <span className="text-sm text-blue-500 font-bold font-mono">{role === 'subscriber' ? '∞' : (adLupas || 0)}</span>
                                            </div>
                                            <div className="flex flex-col justify-center items-center bg-neutral-900 p-1.5 rounded border border-neutral-800">
                                                <span className="flex items-center gap-0.5 text-[8px] md:text-[9px] text-gray-400 font-mono mb-1"><Search className="w-2.5 h-2.5 text-green-500" /> EXTRAS</span>
                                                <span className="text-sm text-green-500 font-bold font-mono">{role === 'subscriber' ? '∞' : (extraLupas || 0)}</span>
                                            </div>
                                            <div className="flex flex-col justify-center items-center bg-neutral-900 p-1.5 rounded border border-neutral-800">
                                                <span className="flex items-center gap-0.5 text-[8px] md:text-[9px] text-amber-500 font-mono mb-1"><Lock className="w-2.5 h-2.5" /> CHAVES</span>
                                                <span className="text-sm text-amber-500 font-bold font-mono">{archiveKeys || 0}</span>
                                            </div>
                                            <div className="col-span-4 flex justify-between items-center bg-neutral-900 p-2 rounded border border-neutral-800">
                                                <span className="flex items-center gap-1.5 text-[9px] text-indigo-400 font-mono"><PlayCircle className="w-3 h-3" /> DESBLOQUEIOS AD (HOJE)</span>
                                                <span className="text-sm text-indigo-400 font-bold font-mono">{dailyAdUnlocks || 0}/3</span>
                                            </div>
                                        </div>
                                        {role !== 'subscriber' && (
                                            <button
                                                onClick={triggerAdWatch}
                                                disabled={adUsesToday >= 5 || (adLupas || 0) >= 13}
                                                title={adUsesToday >= 5 ? "Esgotado por hoje" : (adLupas || 0) >= 13 ? "Limite de Inventário Atingido" : "Recupere 3 lupas assistindo a um anúncio"}
                                                className="text-[10px] bg-indigo-900/40 hover:bg-indigo-900/80 text-indigo-300 border border-indigo-500/30 w-full justify-center px-3 py-2.5 rounded flex items-center gap-2 transition-colors uppercase font-bold tracking-widest disabled:opacity-50 disabled:cursor-not-allowed"
                                            >
                                                <PlayCircle className="w-3.5 h-3.5" /> Anúncio p/ Lupas {adUsesToday < 5 ? `(${5 - adUsesToday} LIMITES)` : `(ESGOTADO)`}
                                            </button>
                                        )}
                                    </div>
                                )}
                                <div className="grid grid-cols-2 gap-3 mb-6">
                                    <div className="bg-neutral-800/50 p-3 rounded border border-neutral-700 text-center">
                                        <div className="text-2xl font-bold text-gray-200">{gameState.archivedCases.filter(c => c.status === 'solved').length}</div>
                                        <div className="text-[10px] text-neutral-500 uppercase tracking-wider">Casos Resolvidos</div>
                                    </div>
                                    <div className="bg-neutral-800/50 p-3 rounded border border-neutral-700 text-center">
                                        <div className="text-2xl font-bold text-gray-200">
                                            {gameState.archivedCases.length > 0 ?
                                                Math.round((gameState.archivedCases.filter(c => c.status === 'solved').length / gameState.archivedCases.length) * 100) : 0}%
                                        </div>
                                        <div className="text-[10px] text-neutral-500 uppercase tracking-wider">Precisão</div>
                                    </div>
                                </div>

                                {/* Resgatar Código VIP */}
                                <div className="mb-6 bg-neutral-800/50 p-4 border border-amber-900/30 rounded-lg">
                                    <label className="block text-xs font-bold text-amber-500 uppercase tracking-widest mb-2 flex items-center gap-2">
                                        <Zap size={14} className="fill-current" /> Resgatar Credencial VIP
                                    </label>
                                    <div className="flex gap-2">
                                        <input
                                            type="text"
                                            placeholder="INSIRA O CÓDIGO"
                                            value={redeemCode}
                                            onChange={e => setRedeemCode(e.target.value.toUpperCase())}
                                            className="flex-1 bg-neutral-900 border border-neutral-700 rounded px-3 py-2 text-sm text-amber-100 font-mono focus:border-amber-500 outline-none uppercase"
                                            disabled={isRedeeming}
                                        />
                                        <button
                                            onClick={handleRedeemCode}
                                            disabled={isRedeeming || !redeemCode.trim()}
                                            className="bg-amber-700 hover:bg-amber-600 disabled:bg-neutral-700 disabled:text-neutral-500 text-white font-bold px-4 py-2 rounded transition-colors text-sm"
                                        >
                                            {isRedeeming ? '...' : 'ATIVAR'}
                                        </button>
                                    </div>
                                </div>

                                <button
                                    onClick={async () => {
                                        if (!confirm("CUIDADO: Isso apagará TODO o seu progresso da nuvem e no dispositivo, perdendo todos os casos e histórico. Deseja zerar seu Detetive e começar de novo?")) return;
                                        setIsLoading(true);
                                        try {
                                            if (authUser && db) {
                                                const docRef = doc(db, "users", authUser.uid);
                                                await updateDoc(docRef, { gameState: null });
                                            }
                                            localStorage.removeItem('chronos_state_v3');
                                            window.location.reload();
                                        } catch (e) {
                                            alert("Erro ao zerar progresso.");
                                            setIsLoading(false);
                                        }
                                    }}
                                    className="w-full bg-red-900/20 border border-red-900/50 text-red-500 py-3 rounded hover:bg-red-900/40 transition-colors flex items-center justify-center gap-2 font-bold uppercase text-sm tracking-wider mb-2"
                                >
                                    <X size={16} /> ZERAR MEU PROGRESSO
                                </button>

                                <button
                                    onClick={handleLogout}
                                    className="w-full bg-neutral-900/20 border border-neutral-700 text-neutral-400 py-3 rounded hover:bg-neutral-800 transition-colors flex items-center justify-center gap-2 font-bold uppercase text-sm tracking-wider"
                                >
                                    <LogOut size={16} /> Encerrar Turno (Logout)
                                </button>
                            </div>
                        </div>
                    </div>
                )
            }

            {isLoading && <div className="fixed inset-0 z-[100] bg-black/80 backdrop-blur-sm flex flex-col items-center justify-center"><Search className="w-16 h-16 text-noir-accent animate-bounce" /><p className="mt-4 text-noir-text font-mono animate-pulse">Processando...</p></div>}
            <div className="fixed top-5 right-5 z-[999] flex flex-col gap-2 w-full max-w-sm px-4 pointer-events-none">
                {notifications.map(n => (
                    <div key={n.id} className={`pointer-events-auto bg-noir-900 border-l-4 p-4 shadow-xl flex items-start gap-3 animate-in fade-in slide-in-from-right-10 duration-300 ${n.type === 'success' ? 'border-green-500' : n.type === 'alert' ? 'border-red-500' : 'border-noir-accent'}`}>
                        {n.type === 'alert' ? <Siren className="w-5 h-5 text-red-500" /> : <Bell className="w-5 h-5" />}
                        <div><h4 className="font-bold text-sm">{n.title}</h4><p className="text-xs">{n.message}</p></div>
                    </div>
                ))}
            </div>

            {/* ANNOUNCEMENT MODAL */}
            {
                announcementModalOpen && (
                    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/80 backdrop-blur-sm p-4 animate-in zoom-in-95">
                        <div className="bg-[#141414] border border-noir-800 rounded-lg max-w-2xl w-full max-h-[85vh] flex flex-col shadow-2xl overflow-hidden">
                            <div className="p-4 border-b border-noir-800 flex justify-between items-center bg-black/40">
                                <h2 className="text-xl font-mono font-bold text-white uppercase flex items-center gap-2">
                                    <Bell className="w-5 h-5 text-amber-500" /> Avisos da Agência
                                </h2>
                                <button onClick={() => setAnnouncementModalOpen(false)} className="text-gray-400 hover:text-white transition-colors">
                                    <X className="w-6 h-6" />
                                </button>
                            </div>
                            <div className="p-6 overflow-y-auto no-scrollbar flex-1 space-y-6">
                                {announcements.length === 0 ? (
                                    <p className="text-center text-gray-500 italic py-10">Nenhum aviso no momento.</p>
                                ) : (
                                    announcements.map(ann => (
                                        <div key={ann.id} className="bg-black/30 border border-noir-800 rounded-lg overflow-hidden">
                                            {ann.imageUrl && (
                                                <div className="w-full h-48 md:h-64 object-cover">
                                                    <img src={ann.imageUrl} alt={ann.title} className="w-full h-full object-cover" />
                                                </div>
                                            )}
                                            <div className="p-5">
                                                <div className="flex justify-between items-start mb-2">
                                                    <h3 className="text-lg md:text-xl font-bold text-white uppercase font-mono">{ann.title}</h3>
                                                    <span className="text-[10px] text-gray-500">{new Date(ann.createdAt?.toMillis() || Date.now()).toLocaleDateString('pt-BR')}</span>
                                                </div>
                                                <p className="text-gray-300 whitespace-pre-wrap text-sm leading-relaxed">{ann.text}</p>
                                            </div>
                                        </div>
                                    ))
                                )}
                            </div>
                            <div className="p-4 border-t border-noir-800 bg-black/40 text-center">
                                <button onClick={() => setAnnouncementModalOpen(false)} className="bg-amber-600 hover:bg-amber-500 text-black px-8 py-2 rounded font-bold uppercase tracking-wider transition-colors w-full md:w-auto">
                                    Entendido
                                </button>
                            </div>
                        </div>
                    </div>
                )
            }

            {/* PUSH NOTIFICATION MODAL */}
            {
                showPushModal && (
                    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/80 backdrop-blur-sm p-4 animate-in zoom-in-95">
                        <div className="bg-noir-900 border-2 border-noir-800 p-8 rounded-lg max-w-md w-full text-center shadow-2xl relative overflow-hidden">
                            <div className="absolute inset-0 bg-[url('data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAQAAAAECAYAAACp8Z5+AAAAIklEQVQIW2NkQAKrVq36zwjjgzjwqgOx4Wy4IsyECAIEaQDX4w/9C/7r+AAAAABJRU5ErkJggg==')] opacity-10 pointer-events-none"></div>
                            <Radio className="w-16 h-16 text-noir-accent mx-auto mb-4 animate-pulse relative z-10" />
                            <h2 className="text-2xl font-bold text-white mb-2 uppercase tracking-widest relative z-10 font-mono">SINTONIZAR RÁDIO</h2>
                            <p className="text-noir-muted mb-6 text-sm relative z-10">A Agência precisa enviar alertas urgentes quando arquivos terminarem de ser descriptografados em segundo plano. Deseja habilitar a comunicação?</p>
                            <div className="flex gap-4 relative z-10">
                                <button onClick={() => setShowPushModal(false)} className="flex-1 py-3 bg-noir-800 text-noir-400 hover:text-white rounded border border-noir-700 transition-colors uppercase font-bold text-xs tracking-wider">Descartar</button>
                                <button onClick={handleAcceptPush} className="flex-1 py-3 bg-amber-600 hover:bg-amber-500 text-black rounded font-bold uppercase tracking-wider transition-colors shadow-[0_0_15px_rgba(217,119,6,0.4)] text-xs">Autorizar</button>
                            </div>
                        </div>
                    </div>
                )
            }

            {/* ARREST MODAL */}
            {
                arrestModalOpen && (
                    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/80 backdrop-blur-sm p-4 animate-in zoom-in-95">
                        <div className="bg-noir-900 border-2 border-red-900 p-6 rounded-lg max-w-md w-full text-center">
                            <h2 className="text-2xl font-bold text-red-500 mb-4">CONFIRMAR PRISÃO</h2>
                            <p className="mb-6">Tem certeza que este indivíduo é o culpado?</p>
                            <div className="flex gap-4">
                                <button onClick={() => setArrestModalOpen(null)} className="flex-1 py-3 bg-noir-800 rounded">CANCELAR</button>
                                <button onClick={() => handleArrest(arrestModalOpen!)} className="flex-1 py-3 bg-red-700 text-white rounded font-bold">PRENDER</button>
                            </div>
                        </div>
                    </div>
                )
            }

            {/* LOG MODAL */}
            {
                selectedLog && (
                    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/80 backdrop-blur-sm p-4 animate-in zoom-in-95">
                        <div className="bg-noir-900 border border-noir-700 p-6 rounded-lg max-w-2xl w-full max-h-[80vh] flex flex-col shadow-2xl relative">
                            <button onClick={() => setSelectedLog(null)} className="absolute top-4 right-4 text-noir-muted hover:text-white bg-noir-800 p-2 rounded-full transition-colors"><X className="w-4 h-4" /></button>
                            <div className="flex items-center gap-3 border-b border-noir-800 pb-4 mb-4">
                                <div className="border border-noir-accent/30 bg-noir-800 px-3 py-2 rounded flex items-center gap-2">
                                    <History className="w-4 h-4 text-amber-500" />
                                    <span className="font-mono text-white text-sm tracking-widest">{selectedLog.timestamp}</span>
                                </div>
                                <div>
                                    <h3 className="text-xl font-mono text-white tracking-wider uppercase flex items-center gap-2">
                                        {selectedLog.type === 'clue' ? <MapIcon className="w-4 h-4 text-noir-accent" /> : <MessageSquare className="w-4 h-4 text-noir-accent" />}
                                        {selectedLog.content}
                                    </h3>
                                </div>
                            </div>
                            <div className="flex-1 overflow-y-auto pr-2 custom-scrollbar text-noir-text leading-relaxed whitespace-pre-wrap font-serif text-lg">
                                {selectedLog.details}
                            </div>
                        </div>
                    </div>
                )
            }

            {/* GLOBAL HEADER (Dashboard Main Only) */}
            {
                gameState.status === GameStatus.DASHBOARD && gameState.dashboardView === 'main' && (
                    <header className="flex justify-between items-center p-4 md:p-6 border-b border-noir-800 bg-noir-900 z-20">
                        <h1 className="text-2xl md:text-4xl font-mono font-bold text-noir-accent tracking-widest uppercase leading-none drop-shadow-[0_0_10px_rgba(217,119,6,0.5)] truncate min-w-0">
                            ARQUIVOS ACME
                        </h1>
                        <div className="flex items-center gap-4">
                            <button onClick={() => {
                                setAnnouncementModalOpen(true);
                                const ids = announcements.map(a => a.id!);
                                localStorage.setItem('chronos_read_announcements', JSON.stringify(ids));
                                setUnreadAnnouncementsCount(0);
                            }} className="relative text-white hover:text-amber-500 transition-colors p-2 cursor-pointer">
                                <Bell className="w-6 h-6" />
                                {unreadAnnouncementsCount > 0 && <span className="absolute 0 top-0 right-0 bg-red-600 text-white text-[10px] w-4 h-4 flex items-center justify-center rounded-full animate-pulse font-bold border border-black">{unreadAnnouncementsCount}</span>}
                            </button>
                            {UserBadge()}
                        </div>
                    </header>
                )
            }

            <main className="flex-1 overflow-hidden relative pb-16 lg:pb-0 flex flex-col h-full">
                {gameState.status === GameStatus.DASHBOARD ? (
                    gameState.dashboardView === 'main' ? renderDashboardMain() :
                        gameState.dashboardView === 'daily_list' ? renderDailyList() :
                            renderAcmeClub()
                ) : (
                    <div className="flex flex-col h-full">

                        {/* CASE HEADER - STICKY & COMPACT */}
                        <div className="sticky top-0 z-[100] bg-noir-900 border-b border-noir-800 shadow-lg flex flex-col md:flex-row justify-between items-center px-4 py-2 shrink-0 gap-2">
                            <div className="flex flex-1 items-center gap-3 overflow-hidden">
                                <button onClick={() => { setInteractionText(''); setGameState(prev => ({ ...prev, status: GameStatus.DASHBOARD, selectedCaseId: null })); }} className="text-noir-muted hover:text-white transition-colors p-1" title="Sair do Caso">
                                    <ArrowRight className="w-4 h-4 rotate-180" />
                                </button>
                                <div className="min-w-0">
                                    <h2 className="text-lg font-mono font-bold text-white uppercase leading-none truncate flex items-center gap-2">
                                        <Briefcase className="w-4 h-4 text-noir-accent shrink-0" />
                                        {activeCase?.title || "DOSSIÊ SEM TÍTULO"}
                                    </h2>
                                </div>
                            </div>

                            <div className="flex items-center gap-4 shrink-0">
                                {/* AD BUTTON (If Not VIP/No Energy) */}
                                {role !== 'subscriber' && (
                                    <button
                                        onClick={triggerAdWatch}
                                        disabled={adUsesToday >= 5}
                                        className="hidden md:flex bg-amber-600/20 hover:bg-amber-600/40 text-amber-500 border border-amber-900/50 px-3 py-1 rounded font-mono font-bold text-xs uppercase transition-colors whitespace-nowrap disabled:opacity-50 disabled:cursor-not-allowed"
                                        title={adUsesToday >= 5 ? "Esgotado por hoje" : "Recupere 3 lupas assistindo a um anúncio"}
                                    >
                                        Interceptar Sinal {adUsesToday < 5 ? `(${5 - adUsesToday}/5)` : '(Esgotado)'}
                                    </button>
                                )}

                                {UserBadge()}
                                {/* STAMP CLOCK AREA */}
                                <div className="relative border border-dashed border-noir-700 bg-black/50 px-3 py-1 rounded flex items-center gap-2">
                                    <Clock gameTime={gameState.gameTime} className="border-none shadow-none bg-transparent p-0 text-xs" />
                                    {timeFeedback && (
                                        <span key={timeFeedback.id} className="absolute -bottom-6 right-0 text-amber-500 font-bold font-mono text-sm animate-bounce drop-shadow-[0_1px_2px_rgba(0,0,0,0.8)]">
                                            {timeFeedback.label}
                                        </span>
                                    )}
                                </div>
                            </div>
                        </div>

                        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 flex-1 min-h-0 p-4">
                            {/* LEFT PANEL (Unified MAP + NOTEBOOK) */}
                            <div className={`lg:col-span-1 bg-noir-900/50 border border-noir-800 rounded flex flex-col h-full overflow-hidden ${activeTab === 'leftPanel' ? 'block' : 'hidden lg:block'}`}>
                                {/* TABS HEADER */}
                                <div className="hidden lg:flex border-b border-noir-800 bg-black/60 overflow-x-auto shrink-0 custom-scrollbar">
                                    <button onClick={() => setLeftPanelTab('map')} className={`flex-1 py-4 px-2 flex flex-col items-center justify-center min-w-[60px] hover:bg-noir-800/50 transition-colors ${leftPanelTab === 'map' ? 'text-amber-500 border-b-2 border-amber-500 bg-noir-800/50 relative' : 'text-noir-muted'}`} title="Deslocamento">
                                        <MapIcon className="w-5 h-5 mb-1" />
                                        <span className="text-[9px] font-bold uppercase tracking-widest font-mono">Mapa</span>
                                    </button>
                                    <button onClick={() => setLeftPanelTab('notes')} className={`flex-1 py-4 px-2 flex flex-col items-center justify-center min-w-[60px] hover:bg-noir-800/50 transition-colors ${leftPanelTab === 'notes' ? 'text-amber-500 border-b-2 border-amber-500 bg-noir-800/50 relative' : 'text-noir-muted'}`} title="Anotações">
                                        <PenTool className="w-5 h-5 mb-1" />
                                        <span className="text-[9px] font-bold uppercase tracking-widest font-mono">Notas</span>
                                    </button>
                                    <button onClick={() => setLeftPanelTab('suspects')} className={`flex-1 py-4 px-2 flex flex-col items-center justify-center min-w-[60px] hover:bg-noir-800/50 transition-colors ${leftPanelTab === 'suspects' ? 'text-amber-500 border-b-2 border-amber-500 bg-noir-800/50 relative' : 'text-noir-muted'}`} title="Pessoas ou Suspeitos">
                                        <User className="w-5 h-5 mb-1" />
                                        <span className="text-[9px] font-bold uppercase tracking-widest font-mono">Pessoas</span>
                                    </button>
                                    <button onClick={() => setLeftPanelTab('clues')} className={`flex-1 py-4 px-2 flex flex-col items-center justify-center min-w-[60px] hover:bg-noir-800/50 transition-colors ${leftPanelTab === 'clues' ? 'text-amber-500 border-b-2 border-amber-500 bg-noir-800/50 relative' : 'text-noir-muted'}`} title="Pistas Físicas e Digitais">
                                        <Fingerprint className="w-5 h-5 mb-1" />
                                        <span className="text-[9px] font-bold uppercase tracking-widest font-mono">Pistas</span>
                                    </button>
                                    <button onClick={() => setLeftPanelTab('history')} className={`flex-1 py-4 px-2 flex flex-col items-center justify-center min-w-[60px] hover:bg-noir-800/50 transition-colors ${leftPanelTab === 'history' ? 'text-amber-500 border-b-2 border-amber-500 bg-noir-800/50 relative' : 'text-noir-muted'}`} title="Histórico de Ações">
                                        <History className="w-5 h-5 mb-1" />
                                        <span className="text-[9px] font-bold uppercase tracking-widest font-mono">Logs</span>
                                    </button>
                                </div>

                                {/* TAB CONTENT */}
                                <div className="p-4 flex-1 overflow-y-auto custom-scrollbar">
                                    {leftPanelTab === 'map' && (
                                        <div className="space-y-4 pb-20">
                                            <LocationCard location={HQ_LOCATION} isCurrentLocation={activeCase?.currentLocationId === 'HQ'} distanceToTravel={calculateDistance(playerCurrentLocation, HQ_LOCATION)} onTravel={startTravel} onEnter={() => { }} disabled={gameState.travelStatus === 'moving' || isNight()} gameTime={gameState.gameTime} />
                                            {activeCase?.locations.filter(l => activeCase.discoveredLocationIds.includes(l.id)).sort((a, b) => calculateDistance(playerCurrentLocation, a) - calculateDistance(playerCurrentLocation, b)).map(loc => (
                                                <LocationCard key={loc.id} location={loc} isCurrentLocation={activeCase.currentLocationId === loc.id} distanceToTravel={calculateDistance(playerCurrentLocation, loc)} onTravel={startTravel} onEnter={() => { }} disabled={gameState.travelStatus === 'moving' || isNight()} gameTime={gameState.gameTime} />
                                            ))}
                                        </div>
                                    )}

                                    {leftPanelTab === 'notes' && <textarea className="w-full h-full min-h-[300px] bg-transparent text-xs font-mono text-noir-text outline-none resize-none" placeholder="Suas anotações pessoais da investigação..." value={activeCase?.userNotes || ''} onChange={e => { if (activeCase) setGameState(prev => ({ ...prev, activeCases: prev.activeCases.map(c => c.id === activeCase.id ? { ...c, userNotes: e.target.value } : c) })); }} />}

                                    {leftPanelTab === 'suspects' && (
                                        activeCase?.characters.filter(c => activeCase.metCharacterIds.includes(c.id)).length === 0 ?
                                            <div className="text-center text-noir-muted text-xs mt-4 flex flex-col items-center opacity-50"><User className="w-6 h-6 mb-2" /><p>Nenhum suspeito encontrado.</p></div> :
                                            activeCase?.characters.filter(c => activeCase.metCharacterIds.includes(c.id)).map(c => {
                                                const isKnown = activeCase.knownCharacterNames[c.id];
                                                return (
                                                    <div key={c.id} className="mb-2 p-3 border border-noir-800 rounded bg-black/20 hover:border-noir-700 transition-colors">
                                                        <div className="flex justify-between items-start">
                                                            <div>
                                                                <span className="text-sm font-bold text-white block">{isKnown ? c.name : "Desconhecido"}</span>
                                                                <span className="text-[10px] text-noir-accent bg-noir-800 px-2 py-0.5 mt-1 rounded uppercase inline-block font-mono tracking-wider">{isKnown ? ROLE_MAP[c.role] : "???"}</span>
                                                            </div>
                                                            {isKnown && c.locationId !== 'HQ' && <button onClick={() => handlePhoneCall(c.id)} className="text-green-500 text-[10px] font-bold uppercase tracking-wider flex items-center gap-1 hover:bg-green-900/30 p-1.5 rounded border border-transparent hover:border-green-900/50 transition-colors"><Phone className="w-3 h-3" /> Ligar</button>}
                                                        </div>
                                                        <p className="text-xs text-noir-muted mt-3 border-t border-noir-800/50 pt-2 italic leading-relaxed">{isKnown ? c.description : "Identidade não confirmada. Investigue mais para descobrir."}</p>
                                                    </div>
                                                );
                                            })
                                    )}

                                    {leftPanelTab === 'clues' && (
                                        <div className="space-y-3">
                                            {/* Inventory Items */}
                                            {activeCase?.inventory?.map((item) => (
                                                <div key={item.id} className="bg-amber-900/20 p-4 rounded-lg border border-amber-900/50 hover:border-amber-600 transition-colors animate-in slide-in-from-left shadow-lg">
                                                    <div className="flex justify-between items-start mb-2">
                                                        <div className="flex items-center gap-2">
                                                            <FolderOpen className="w-4 h-4 text-amber-500" />
                                                            <h4 className="font-bold text-amber-500 font-mono text-[11px] uppercase tracking-widest">{item.name}</h4>
                                                        </div>
                                                        <span className="text-[9px] bg-black/50 text-gray-400 px-2 py-0.5 rounded font-mono">ENCONTRADO: {item.foundAt}</span>
                                                    </div>
                                                    <p className="text-sm font-serif leading-relaxed text-gray-300 mb-3">"{item.description}"</p>
                                                    {item.isInspectable ? (
                                                        <div className="text-right">
                                                            <button 
                                                                onClick={() => handleInspectItem(item)}
                                                                disabled={gameState.travelStatus === 'moving' || isNight()}
                                                                className="bg-amber-600 hover:bg-amber-500 text-black px-4 py-1.5 rounded text-xs font-bold uppercase tracking-widest transition-colors disabled:opacity-50 disabled:cursor-not-allowed shadow-md"
                                                            >
                                                                <Search className="w-3 h-3 inline mr-1" /> Inspecionar (+30min)
                                                            </button>
                                                        </div>
                                                    ) : (
                                                        <div className="text-right text-[10px] text-green-500 font-mono font-bold uppercase tracking-widest">
                                                            ✓ Analisado
                                                        </div>
                                                    )}
                                                </div>
                                            ))}

                                            {/* Normal Logs */}
                                            {[...(activeCase?.logs || [])].filter(l => l.type === 'clue').map((log, i) => (
                                                <div key={i} className="bg-black/30 p-4 rounded-lg border border-noir-800 hover:border-noir-600 transition-colors animate-in slide-in-from-left">
                                                    <div className="flex items-center gap-2 mb-2">
                                                        <Fingerprint className="w-4 h-4 text-noir-500" />
                                                        <h4 className="font-bold text-noir-500 font-mono text-[11px] opacity-90 uppercase tracking-[0.2em]">{log.content}</h4>
                                                    </div>
                                                    <p className="text-sm italic font-serif leading-relaxed text-noir-300">"{log.details}"</p>
                                                </div>
                                            ))}
                                            
                                            {(!activeCase?.logs || activeCase.logs.filter(l => l.type === 'clue').length === 0) && (!activeCase?.inventory || activeCase.inventory.length === 0) && (
                                                <div className="text-center text-noir-muted text-xs mt-10 flex flex-col items-center opacity-50"><Search className="w-6 h-6 mb-2" /><p>Nenhuma evidência recolhida ainda.<br />Vasculhe locais ou interrogue testemunhas.</p></div>
                                            )}
                                        </div>
                                    )}

                                    {leftPanelTab === 'history' && (
                                        <div className="space-y-3">
                                            <p className="text-[10px] text-noir-muted/70 text-center mb-4 italic font-mono uppercase tracking-widest">Clique em um registro para expandir</p>
                                            {[...(activeCase?.logs || [])].reverse().map((log, i) => (
                                                <div key={i} onClick={() => setSelectedLog(log)} className="bg-noir-900/40 p-3 rounded-lg border border-noir-800 cursor-pointer hover:border-amber-900/50 hover:bg-black/40 transition-all animate-in fade-in group">
                                                    <div className="flex items-center gap-2 mb-2 justify-between">
                                                        <div className="flex items-center gap-2">
                                                            <span className={`text-[9px] px-2 py-0.5 rounded font-mono uppercase font-bold tracking-widest ${log.type === 'clue' ? 'bg-amber-900/30 text-amber-500 border border-amber-900/50' : log.type === 'dialogue' ? 'bg-blue-900/20 text-blue-400' : 'bg-noir-800 text-noir-400'}`}>
                                                                {log.type === 'clue' ? 'PISTA' : log.type === 'dialogue' ? 'FALA' : 'SISTEMA'}
                                                            </span>
                                                            <span className="text-[10px] text-noir-500 font-mono flex items-center gap-1">
                                                                <History className="w-3 h-3 text-noir-600" /> {log.timestamp}
                                                            </span>
                                                        </div>
                                                        <Maximize2 className="w-3 h-3 opacity-0 group-hover:opacity-100 text-amber-600 transition-opacity" />
                                                    </div>
                                                    <span className="font-bold text-sm text-gray-200 block mb-1 leading-tight">{log.content}</span>
                                                    <p className="text-xs text-noir-500 font-serif line-clamp-2 italic leading-relaxed">{log.details}</p>
                                                </div>
                                            ))}
                                            {(!activeCase?.logs || activeCase.logs.length === 0) && (
                                                <div className="text-center text-noir-muted text-xs mt-10 flex flex-col items-center opacity-50"><History className="w-6 h-6 mb-2" /><p>Nenhuma ação registrada neste caso.</p></div>
                                            )}
                                        </div>
                                    )}
                                </div>
                            </div>

                            {/* Middle: Investigation */}
                            <div className={`lg:col-span-2 flex flex-col h-full overflow-hidden ${activeTab === 'investigation' ? 'block' : 'hidden lg:block'}`}>

                                {/* NIGHT OVERLAY */}
                                {isNight() && gameState.travelStatus !== 'moving' && (
                                    <div className="absolute inset-0 z-30 bg-black/90 backdrop-blur-sm flex flex-col items-center justify-center p-8 text-center animate-in fade-in duration-500">
                                        <Moon className="w-20 h-20 text-indigo-400 mb-6 drop-shadow-[0_0_15px_rgba(129,140,248,0.5)]" />
                                        <h2 className="text-3xl font-mono font-bold text-white mb-2 uppercase tracking-widest">EXPEDIENTE ENCERRADO</h2>
                                        <p className="text-noir-muted max-w-md mb-8">
                                            São mais de 22:00. A cidade dorme.
                                        </p>
                                        <button
                                            onClick={handleSleep}
                                            className="px-8 py-4 bg-indigo-900 hover:bg-indigo-800 text-white font-bold rounded-lg border border-indigo-700 shadow-[0_0_20px_rgba(79,70,229,0.3)] transition-all transform hover:scale-105"
                                        >
                                            DORMIR ATÉ 08:00
                                        </button>
                                    </div>
                                )}

                                {gameState.travelStatus === 'moving' ? (
                                    /* TRAVEL SCREEN */
                                    <div className="flex-1 flex flex-col items-center justify-center p-8 bg-noir-900 border border-noir-800 rounded-lg shadow-2xl relative overflow-hidden animate-in fade-in zoom-in-95 duration-500">
                                        <div className="absolute inset-0 bg-noir-accent/5 pointer-events-none"></div>
                                        <button onClick={skipTravel} className="absolute top-4 right-4 text-xs bg-red-900/30 text-red-500 border border-red-900/50 p-2 rounded hover:bg-red-900/50 z-20">DEBUG: PULAR</button>

                                        <Car className="w-20 h-20 text-noir-accent mb-6 animate-pulse" />
                                        <h2 className="text-3xl font-mono font-bold mb-2 text-white uppercase tracking-widest">EM TRÂNSITO</h2>

                                        <p className="text-xl text-noir-muted mb-6">
                                            Avançando relógio...
                                        </p>

                                        <div className="w-full max-w-md bg-noir-800 h-6 rounded-none border border-noir-700 overflow-hidden relative">
                                            <div className="absolute inset-0 z-10 bg-[url('data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAQAAAAECAYAAACp8Z5+AAAAIklEQVQIW2NkQAKrVq36zwjjgzjwqgOx4Wy4IsyECAIEaQDX4w/9C/7r+AAAAABJRU5ErkJggg==')] opacity-20"></div>
                                            <div className="bg-noir-accent h-full transition-all duration-1000 relative shadow-[0_0_15px_rgba(217,119,6,0.6)]" style={{ width: `${100 - (timeLeft / (gameState.travelTotalDuration || 1) * 100)}%` }}></div>
                                        </div>
                                    </div>
                                ) : (
                                    <div className="flex-1 bg-noir-900 border border-noir-700 rounded p-6 overflow-y-auto flex flex-col">
                                        {/* Location Header */}
                                        <div className="border-b border-noir-700 pb-4 mb-4">
                                            <h2 className="text-2xl text-white font-mono">
                                                {activeCase?.currentLocationId === 'HQ' ? 'QUARTEL GENERAL' : activeCase?.locations.find(l => l.id === activeCase.currentLocationId)?.name}
                                            </h2>
                                            {activeCase?.locations.find(l => l.id === activeCase.currentLocationId)?.isCrimeScene && (
                                                <span className="bg-yellow-600 text-black text-xs font-bold px-2 py-1 rounded inline-flex items-center gap-1 mt-2">
                                                    <BadgeAlert className="w-3 h-3" /> CENA DO CRIME - ACESSO LIVRE
                                                </span>
                                            )}
                                        </div>

                                        {/* Dynamic Content */}
                                        {interactionText && (
                                            <div className="bg-noir-800 p-4 rounded border-l-4 border-noir-accent mb-4 relative">
                                                <button onClick={() => setInteractionText('')} className="absolute top-2 right-2"><X className="w-4 h-4 text-noir-muted hover:text-white" /></button>
                                                <p className="whitespace-pre-wrap text-sm leading-relaxed">{interactionText}</p>
                                            </div>
                                        )}

                                        {activeCase?.currentLocationId === 'HQ' ? (
                                            <div className="space-y-4">
                                                <div className="bg-noir-800/30 p-4 rounded border border-noir-700 shadow-lg">
                                                    <h3 className="text-noir-accent font-bold mb-3 uppercase text-sm border-b border-noir-700 pb-1 flex items-center gap-2">
                                                        <Briefcase className="w-4 h-4" /> Arquivo do Caso
                                                    </h3>
                                                    <p className="text-sm whitespace-pre-wrap text-noir-text leading-relaxed font-mono">{activeCase.description}</p>
                                                </div>
                                                <div className="mt-8">
                                                    <h3 className="text-white font-bold mb-4 flex items-center gap-2 border-b border-noir-800 pb-2">
                                                        <Phone className="w-4 h-4 text-green-500" /> CENTRAL TELEFÔNICA
                                                    </h3>

                                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                                                        {activeCase.characters.filter(c => activeCase.metCharacterIds.includes(c.id)).length === 0 ? (
                                                            <div className="col-span-full text-center py-6 border border-dashed border-noir-800 rounded bg-noir-900/50">
                                                                <User className="w-8 h-8 mx-auto text-noir-700 mb-2" />
                                                                <p className="text-noir-muted text-sm">Nenhum contato disponível.</p>
                                                                <p className="text-[10px] text-noir-600">Investigue locais para encontrar testemunhas e suspeitos.</p>
                                                            </div>
                                                        ) : (
                                                            activeCase.characters.filter(c => activeCase.metCharacterIds.includes(c.id)).map(char => (
                                                                <div key={char.id} className="bg-black/30 p-3 rounded flex justify-between items-center border border-noir-700 hover:border-noir-600 transition-colors">
                                                                    <div>
                                                                        <span className="text-sm font-bold text-white block">{char.name}</span>
                                                                        <span className="text-[10px] text-noir-muted flex items-center gap-1">
                                                                            {char.arrivalAtHQ ?
                                                                                (gameState.gameTime >= char.arrivalAtHQ ? <span className="text-green-500">● NO SAGUÃO</span> : <span className="text-amber-500">● A CAMINHO</span>) :
                                                                                (char.locationId === 'HQ' ? <span className="text-green-500">● NO SAGUÃO</span> : <span className="text-noir-600">● AUSENTE</span>)
                                                                            }
                                                                        </span>
                                                                    </div>
                                                                    <button
                                                                        onClick={() => handlePhoneCall(char.id)}
                                                                        disabled={char.locationId === 'HQ' || isLoading || isNight()}
                                                                        className={`p-2 rounded transition-all ${char.locationId === 'HQ' ? 'bg-green-900/10 text-green-700 cursor-default' : 'bg-noir-800 hover:bg-noir-700 text-green-500 hover:scale-110 shadow-lg shadow-green-900/20'}`}
                                                                        title={char.locationId === 'HQ' ? "Já está no QG" : "Ligar"}
                                                                    >
                                                                        <PhoneCall className="w-4 h-4" />
                                                                    </button>
                                                                </div>
                                                            ))
                                                        )}
                                                    </div>
                                                </div>
                                            </div>
                                        ) : (
                                            <div>
                                                <button
                                                    onClick={() => investigateLocation(activeCase!.locations.find(l => l.id === activeCase?.currentLocationId)!)}
                                                    className={`w-full py-6 border-2 border-dashed mb-6 flex flex-col items-center gap-2 transition-all group ${isScouted ? 'bg-noir-900 border-noir-800 text-noir-700 cursor-default' : 'bg-noir-800 border-noir-600 hover:border-noir-accent text-noir-muted hover:text-white'}`}
                                                >
                                                    {isScouted ? (
                                                        <>
                                                            <Search className="w-8 h-8 opacity-50" />
                                                            <span className="font-bold">LOCAL JÁ VASCULHADO</span>
                                                            <span className="text-xs">Personagens presentes identificados.</span>
                                                        </>
                                                    ) : (
                                                        <>
                                                            <Search className="w-8 h-8" />
                                                            <span className="font-bold">VASCULHAR LOCAL (+1h)</span>
                                                            <span className="text-xs group-hover:text-amber-500">Encontrar pistas e pessoas escondidas</span>
                                                        </>
                                                    )}
                                                </button>
                                            </div>
                                        )}

                                        {/* Characters at Location (Neblina de Guerra) */}
                                        {!isScouted && activeCase?.currentLocationId !== 'HQ' ? (
                                            <div className="flex flex-col items-center justify-center p-8 bg-black/20 rounded border border-noir-800 border-dashed text-noir-700">
                                                <EyeOff className="w-12 h-12 mb-2" />
                                                <p className="text-sm font-mono">Presença desconhecida.</p>
                                                <p className="text-xs">Vasculhe o local para identificar quem está aqui.</p>
                                            </div>
                                        ) : (
                                            activeCase?.characters.filter(c => c.locationId === activeCase.currentLocationId).map(char => {
                                                const isKnown = activeCase.knownCharacterNames[char.id];
                                                const displayName = isKnown ? char.name : "Indivíduo Suspeito";
                                                const displayRole = isKnown ? ROLE_MAP[char.role] : "???";

                                                return (
                                                    <div key={char.id} className="bg-black/40 p-4 rounded border border-noir-700 mt-4 animate-in slide-in-from-bottom duration-500">
                                                        <div className="flex justify-between items-start">
                                                            <div>
                                                                <span className={`font-bold ${isKnown ? 'text-white' : 'text-noir-muted italic'}`}>{displayName}</span>
                                                                <span className="text-xs bg-noir-800 px-2 py-1 rounded">{displayRole}</span>
                                                            </div>
                                                            {!isKnown && <p className="text-xs text-noir-500 mb-2">Fale com esta pessoa para descobrir sua identidade.</p>}

                                                            <div className="flex gap-2 mt-2">
                                                                <input type="text" placeholder="Fazer pergunta..." value={inputQuery} onChange={e => setInputQuery(e.target.value)} className="flex-1 bg-noir-900 border border-noir-700 rounded p-2 text-xs text-white outline-none focus:border-noir-accent" />
                                                                <button
                                                                    onClick={() => handleInterrogation(char.id, inputQuery)}
                                                                    disabled={cooldownLeft > 0 || isLoading || isNight()}
                                                                    className={`px-3 rounded text-noir-accent flex items-center gap-1 min-w-[60px] justify-center transition-all ${cooldownLeft > 0 ? 'bg-noir-800 cursor-not-allowed opacity-50' : 'bg-noir-800 hover:bg-noir-700'}`}
                                                                    title="+30min"
                                                                >
                                                                    {cooldownLeft > 0 ? <span className="font-mono">{cooldownLeft}s</span> : <MessageSquare className="w-4 h-4" />}
                                                                </button>

                                                                {isKnown && char.role === 'suspect' && (
                                                                    <button onClick={() => setArrestModalOpen(char.id)} className="bg-red-900/20 text-red-500 px-3 rounded border border-red-900/50 hover:bg-red-900/40" title="Emitir Mandado de Prisão"><Siren className="w-4 h-4" /></button>
                                                                )}
                                                            </div>
                                                        </div>
                                                    </div>
                                                );
                                            })
                                        )}
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                )}

                {/* Mobile Bottom Navigation (Same as before) */}
                {gameState.status !== GameStatus.DASHBOARD && (
                    <div className="lg:hidden fixed bottom-0 left-0 right-0 bg-noir-950 border-t border-noir-800 z-50 flex justify-around p-1 pb-safe shadow-[0_-10px_20px_rgba(0,0,0,0.8)]">
                        <button onClick={() => setActiveTab('investigation')} className={`flex-1 flex flex-col items-center gap-1 p-2 rounded-lg transition-colors ${activeTab === 'investigation' ? 'text-amber-500 bg-black shadow-inner shadow-amber-900/30 border-t-2 border-amber-500' : 'text-noir-500 hover:text-noir-300'}`}>
                            <Search className="w-5 h-5 mb-0.5" />
                            <span className="text-[9px] font-bold uppercase tracking-widest font-mono">Cena</span>
                        </button>
                        <button onClick={() => { setActiveTab('leftPanel'); setLeftPanelTab('map'); }} className={`flex-1 flex flex-col items-center gap-1 p-2 rounded-lg transition-colors ${activeTab === 'leftPanel' && leftPanelTab === 'map' ? 'text-amber-500 bg-black shadow-inner shadow-amber-900/30 border-t-2 border-amber-500' : 'text-noir-500 hover:text-noir-300'}`}>
                            <MapIcon className="w-5 h-5 mb-0.5" />
                            <span className="text-[9px] font-bold uppercase tracking-widest font-mono">Mapa</span>
                        </button>
                        <button onClick={() => { setActiveTab('leftPanel'); setLeftPanelTab('notes'); }} className={`flex-1 flex flex-col items-center gap-1 p-2 rounded-lg transition-colors ${activeTab === 'leftPanel' && leftPanelTab === 'notes' ? 'text-amber-500 bg-black shadow-inner shadow-amber-900/30 border-t-2 border-amber-500' : 'text-noir-500 hover:text-noir-300'}`}>
                            <PenTool className="w-5 h-5 mb-0.5" />
                            <span className="text-[9px] font-bold uppercase tracking-widest font-mono">Notas</span>
                        </button>
                        <button onClick={() => { setActiveTab('leftPanel'); setLeftPanelTab('suspects'); }} className={`flex-1 flex flex-col items-center gap-1 p-2 rounded-lg transition-colors ${activeTab === 'leftPanel' && leftPanelTab === 'suspects' ? 'text-amber-500 bg-black shadow-inner shadow-amber-900/30 border-t-2 border-amber-500' : 'text-noir-500 hover:text-noir-300'}`}>
                            <User className="w-5 h-5 mb-0.5" />
                            <span className="text-[9px] font-bold uppercase tracking-widest font-mono">Pessoas</span>
                        </button>
                        <button onClick={() => { setActiveTab('leftPanel'); setLeftPanelTab('clues'); }} className={`flex-1 flex flex-col items-center gap-1 p-2 rounded-lg transition-colors ${activeTab === 'leftPanel' && leftPanelTab === 'clues' ? 'text-amber-500 bg-black shadow-inner shadow-amber-900/30 border-t-2 border-amber-500' : 'text-noir-500 hover:text-noir-300'}`}>
                            <Fingerprint className="w-5 h-5 mb-0.5" />
                            <span className="text-[9px] font-bold uppercase tracking-widest font-mono">Pistas</span>
                        </button>
                        <button onClick={() => { setActiveTab('leftPanel'); setLeftPanelTab('history'); }} className={`flex-1 flex flex-col items-center gap-1 p-2 rounded-lg transition-colors ${activeTab === 'leftPanel' && leftPanelTab === 'history' ? 'text-amber-500 bg-black shadow-inner shadow-amber-900/30 border-t-2 border-amber-500' : 'text-noir-500 hover:text-noir-300'}`}>
                            <History className="w-5 h-5 mb-0.5" />
                            <span className="text-[9px] font-bold uppercase tracking-widest font-mono">Logs</span>
                        </button>
                    </div>
                )}
            </main>
            
            {/* WELCOME TUTORIAL POPUP */}
            {showWelcomeTutorial && (
                <div className="fixed inset-0 z-[300] bg-black/90 backdrop-blur-md flex items-center justify-center p-4 animate-in fade-in duration-500">
                    <div className="bg-[#111] border-2 border-amber-500/50 max-w-lg w-full rounded-2xl shadow-[0_0_50px_rgba(245,158,11,0.2)] overflow-hidden relative">
                        {/* Background Decoration */}
                        <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-transparent via-amber-500 to-transparent"></div>
                        
                        <div className="p-8 md:p-10 text-center">
                            <div className="w-20 h-20 bg-amber-500/10 rounded-full flex items-center justify-center mx-auto mb-6 border border-amber-500/20 shadow-[0_0_20px_rgba(245,158,11,0.1)]">
                                <Briefcase className="w-10 h-10 text-amber-500 animate-pulse" />
                            </div>
                            
                            <h2 className="text-3xl font-black font-mono text-white mb-4 uppercase tracking-tighter leading-none italic">
                                Bem-vindo à <span className="text-amber-500">Agência ACME</span>
                            </h2>
                            
                            <p className="text-gray-400 text-sm md:text-base leading-relaxed mb-8 font-serif">
                                Agente, identificamos que esta é sua primeira vez acessando nossos arquivos confidenciais. 
                                Recomendamos fortemente que realize o treinamento operacional para se familiarizar com nossos protocolos de investigação.
                            </p>
                            
                            <div className="flex flex-col gap-3">
                                <button 
                                    onClick={() => {
                                        setShowWelcomeTutorial(false);
                                        addLog('system', 'Treinamento Operacional iniciado pelo Agente.', 'Iniciando tutorial...');
                                        createCase(true);
                                        // Mark as viewed
                                        if (db && authUser) {
                                            const userRef = doc(db, 'users', authUser.uid);
                                            updateDoc(userRef, { viewedTutorials: [...(gameState.viewedTutorials || []), 'tut_welcome'] });
                                        }
                                    }}
                                    className="bg-amber-600 hover:bg-amber-500 text-black font-black uppercase tracking-widest py-4 rounded-xl transition-all transform hover:scale-[1.02] active:scale-95 shadow-lg flex items-center justify-center gap-2"
                                >
                                    <PlayCircle className="w-6 h-6 fill-current" /> COMEÇAR TREINAMENTO
                                </button>
                                
                                <button 
                                    onClick={() => {
                                        setShowWelcomeTutorial(false);
                                        if (db && authUser) {
                                            const userRef = doc(db, 'users', authUser.uid);
                                            updateDoc(userRef, { viewedTutorials: [...(gameState.viewedTutorials || []), 'tut_welcome'] });
                                        }
                                    }}
                                    className="text-gray-500 hover:text-white font-bold uppercase tracking-widest py-3 text-xs transition-colors"
                                >
                                    Pular e Ir para os Casos
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* ONBOARDING */}
            {gameState.status === GameStatus.INVESTIGATING && (
                <OnboardingPopups 
                    gameState={gameState} 
                    currentTab={activeTab} 
                    leftPanelTab={leftPanelTab} 
                    onCompleteTutorial={(tutId) => {
                        if (db && authUser) {
                            const userRef = doc(db, 'users', authUser.uid);
                            updateDoc(userRef, { viewedTutorials: [...(gameState.viewedTutorials || []), tutId] });
                        }
                    }} 
                />
            )}
        </div >
    );
};
